//#region Import
import { drawPoint, drawRectangle } from "../draw/draw";
import {
  drawOutlines,
  destroyOutlines,
  getDrawBox2D,
  switchOutlineListening,
} from "./slice";
import { processDicomZipData } from "../vtk/Dicom3dManager";
import { updateDisplayNumberAndName } from "@/components/tooth/teethNaming.js";
import {
  ToothNumberMode,
  initToothNumberControls,
  drawTeethNumbers,
  removeTeethNumbers,
} from "../tooth/number/ToothNumberMode";
import { showToothNumberDialog } from "../tooth/number/ToothNumberDialog.vue";

import {
  drawAnnotations,
  destroyAnnotationShapes,
  hexToCanvasColor,
  getAnnotationData,
  getAnnotationGeometry,
  saveAnnotationNrrd,
  saveAllAnnotationNrrds,
  calculateAllFigures,
} from "./annotation";

import { loadNrrdBones, loadNrrdNerves } from "./NrrdHelper";
import { loadNervePoints, updateNervePoints } from "./DicomNerves";

import { sendDicomSegmentation, readDicomSegmentation } from "@/api/dicomApi";
import DicomViewer from "./DicomViewer.vue";
import SimpleDicomViewer from "./controls/SimpleDicomViewer.vue";
import DicomAnnotation from "./controls/DicomAnnotation.vue";
import AssignAnnotation from "./assign/AssignAnnotation.vue";
import { mutationTypes } from "@/store/modules/dicom";
import store from "@/store";
import {
  focusTooth,
  unfocusTooth,
} from "@/components/tooth/focus/ToothFocusMode";
import {
  switchAuxiliaryView,
  focusAuxilaryModel,
  updateAuixiliaryMesh,
} from "./AuxiliaryManager";
import { enterAnnotationMode, leaveAnnotationMode } from "./AnnotationMode";
import {
  switchStitching,
  displayLastStitchingResult,
} from "@/components/appStart/stitching";
import { updateToothDentition } from "@/components/appStart/adjustModel";
import {
  findSignedAngle,
  degreesToRadians,
  radiansToDegrees,
} from "@/components/math.js";
import swal from "sweetalert";
let dwv = require("dwv");
//#endregion

//#region Fields

let _initialized = false;
let _annotationInitialized = false;
let _boneAnnotationsInitialized = false;

let _appArr = [new dwv.App(), new dwv.App(), new dwv.App()];
let _annotationApp = null;
let _childApps = [];
let _orientations = ["axial", "sagittal", "coronal"];
let _colors = ["red", "blue", "green"];
let _vFroms = [
  new THREE.Vector3(0, 1, 0),
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(0, 0, 1),
];
let _dicomBox;
let _originBox = null;
let _menuToothNode = null;
let _menuAddNode = null;
let _nerveAnchorMenu = null;
let _colorsUpdated = false;
let _loadedCounter = 0;

let clonedAnnotations = null;

let isProjectionMoving = false;
let isProjectionRotating = false;
let projectionRotateStartX = 0;
let projectionRotateStartY = 0;
let projectionRotateAngle = null;

let projectionOffsetX = 0;
let projectionOffsetY = 0;
let projectionCoordX = 0;
let projectionCoordY = 0;
let projectionTarget = null;
let projectionLineStart = null;
let projectionLineEnd = null;

let x3dRotation = 0;
let y3dRotation = 0;
let z3dRotation = 0;
let globalRotationMatrix = null;

const appsRotatedSet = new Set();
const identityMatrix = new THREE.Matrix4();

//#endregion

function initDwvCommons() {
  _menuToothNode = document.getElementById("annotation-tooth-menu");
  _menuAddNode = document.getElementById("annotation-add-menu");
  _nerveAnchorMenu = document.getElementById("annotation-nerve-anchor-menu");

  // dwv.luts = {
  //     plain: dwv.luts.plain,
  //     invPlain: dwv.luts.invPlain,
  //     rainbow: dwv.luts.rainbow,
  //     hot: dwv.luts.hot,
  //     hot_iron: dwv.luts.hot_iron,
  //     pet: dwv.luts.pet,
  //     hot_metal_blue: dwv.luts.hot_metal_blue,
  //     pet_20step: dwv.luts.pet_20step
  // };
  dwv.defaultpresets = {};
  dwv.defaultpresets.CT = {
    mediastinum: { center: 40, width: 400 },
    lung: { center: -500, width: 1500 },
    bone: { center: 500, width: 2000 },
    brain: { center: 40, width: 80 },
    head: { center: 90, width: 350 },
  };

  // dwv.logger.level = dwv.logger.levels.INFO;
  // dwv.defaultPresets.PT = {
  //     'suv5-10': { center: 5, width: 10 },
  //     'suv6-8': { center: 6, width: 8 }
  // };
  dwv.decoderScripts.jpeg2000 = "js/decoders/pdfjs/decode-jpeg2000.js";
  dwv.decoderScripts["jpeg-lossless"] =
    "js/decoders/rii-mango/decode-jpegloss.js";
  dwv.decoderScripts["jpeg-baseline"] =
    "js/decoders/pdfjs/decode-jpegbaseline.js";
  dwv.decoderScripts.rle = "js/decoders/dwv/decode-rle.js";

  document.addEventListener("mouseup", () => {
    isProjectionMoving = isProjectionRotating = false;
  });
  document.addEventListener("mouseleave ", () => {
    isProjectionMoving = isProjectionRotating = false;
  });
}

async function loadDicoms(callbackAfterLoad) {
  if (!_initialized) {
    window.onbeforeunload = function () {
      let hasChanges = false;

      _appArr.forEach((app) => {
        if (app.annotations) {
          let changedAnnotations = app.annotations.filter((a) => a.wasChanged);
          hasChanges =
            (app.deletedAnnotations && app.deletedAnnotations.length) ||
            changedAnnotations.length;
        }
      });

      if (hasChanges) {
        // Recommended
        event.preventDefault();
        // Included for legacy support, e.g. Chrome/Edge < 119
        event.returnValue = true;
      }
    };

    _initialized = true;
    console.log("loadDicoms start");
    initDwvCommons();

    _appArr.forEach((app, index) => {
      app.isOnlyDicom = vueApp.isOnlyDicom;
      app.isMobileDevice = vueApp.isMobileDevice; //v-if="!isMobileDevice"
      app.index = index;
      app.isSaggital = index === 1;
      app.isCeph = false;
      app.cephImgData = null;
      app.isNoTeeth = vueApp.isNoTeeth;
      app.isReadonly = vueApp.isViewMode;
      app.viewIndex = app.index !== 0 ? app.index - 1 : 2;
      app.color = _colors[index];
      app.vFrom = _vFroms[index];
      app.orientation = _orientations[index];
      app.divId = `${app.orientation}_viewer`;
      app.toolName = "dragDrop";
      app.editMode = "brush"; //"bezier"; "brush";
      app.editSubMode = "draw";
      app.opacity = 0.8;
      app.sliceOpacity = 0.25;
      app.outlineThickness = 1;
      app.annotationColor = "#CC5500";
      app.segmentationType = "outlines";
      app.segmentations = [
        "mandTeeth",
        "maxTeeth",
        "maxilla",
        "mandible",
        "airway",
        "nerve",
        "gingiva",
      ];
      app.showSegmentation = true;
      app.showIntersection = true;
      app.show3DLayer = true;
      app.isLoading = false;
      app.isAnnotating = false;
      app.isEditingAnnotation = false;
      app.isNoSmoothCrown = true;
      app.editedAnnotation = null;
      app.focusedAnnotation = null;
      app.selectedAnnotation = null;
      app.show3DFiller = true;
      app.annotationFillSegmentations = true;
      app.annotationOpacity = 0.5;
      app.text1 = app.text2 = app.text3 = "";
      app.is3D = false;
      app.isDicom3DView = false;
      app.showTeethNumbers = false;
      app.isSaving = false;
      app.visible = false;

      app.isMultiLayer = true;
      (app.isReplaceLayers = false), (app.upLayerThickness = 0);
      app.downLayerThickness = 0;
      app.isEditAllLayers = false;

      app.lrValue = 0;
      app.paValue = 0;
      app.isValue = 0;
      app.isRotationApplied = false;

      app.loadError = null;

      const viewerInstance = new Vue({
        ...DicomViewer,
        propsData: {
          app: app,
        },
      });
      const vueContainer = document.createElement("div");
      document.getElementById("views_cont").appendChild(vueContainer);
      viewerInstance.$mount(vueContainer);
    });

    window.addEventListener("keydown", (event) => onKeyDown(event));

    _appArr[0].addEventListener("loadend", (event) => {
      if (viewer) viewer.controls.keysEnabled = false;
      console.log(`dicom loadend`, event);
      // _loadedCounter++;
      const app0 = _appArr[0];

      if (app0.loadErrors && app0.loadErrors.length > 0) {
        $id("loader").style.display = "none";
        _appArr.forEach((app, index) => {
          app.loadError = "Case cannot be displayed";
        });
        if (vueApp.isOnlyDicom) {
          swal({
            text: "Case cannot be displayed yet. Please retry in a few minutes",
            buttons: {
              yes: { text: "Retry", value: true },
            },
          }).then(async (willRetry) => {
            if (willRetry) {
              console.log("dicom retry");
              window.self.location.reload(true);
            }
          });
        }
        return;
      }
      let image0 = app0.getLastImage(); //getImage(0);
      if (!image0) {
        const layerGroup = app0.getLayerGroupByDivId(app0.divId);
        const activeLayer = layerGroup.getActiveViewLayer();
        const viewController = activeLayer.getViewController();
        image0 = viewController.getViewImage();
      }

      app0.isOrcaLoaded = true;
      for (let index = 1; index < _appArr.length; index++) {
        const app = _appArr[index];
        app.addEventListener("loadend", (event) => {
          console.log(`dicom loadend`, event);
          app.isOrcaLoaded = true;
          let loadedCount = _appArr.filter((x) => x.isOrcaLoaded).length;
          // _loadedCounter++;
          // if (_loadedCounter == _appArr.length) {
          if (loadedCount == _appArr.length) {
            vueApp.isDicomLoaded = true;
            if (callbackAfterLoad) {
              callbackAfterLoad();
            }
            $id("loader").style.display = "none";
          }
        });

        app.addNewImage(image0.clone());
      }
    });

    vueApp.$on("colorChanged", (target) => {
      console.log(`colorChanged handler`, target);
      _appArr.forEach((app) => {
        setSliceIndex(app, app.current - 1);
        if (app.annotations) {
          let item = vueApp.allItems.find((x) => x.id === target);
          if (!item) {
            item = target;
          }
          let annotation = app.annotations.find((x) => x.model == item);
          if (annotation) {
            annotation.color = vueApp.getActualColor(item);
            annotation.opacityColor = hexToCanvasColor(annotation.color, 0.7);

            if (annotation.isTooth && annotation.isVisible) {
              annotation.figures.forEach((figure) => {
                figure.color = annotation.opacityColor;
                if (figure.spline) {
                  const mainFigure = figure.spline.getChildren()[0];
                  if (mainFigure) {
                    mainFigure.fill(figure.color);
                    mainFigure.stroke(figure.color);
                  }
                }
              });
              if (annotation.childAnnotations) {
                annotation.childAnnotations
                  .filter((a) => a.figures)
                  .forEach((a) => {
                    a.color = annotation.color;
                    a.opacityColor = annotation.opacityColor;
                    a.figures.forEach((figure) => {
                      figure.color = annotation.opacityColor;
                      if (figure.spline) {
                        const mainFigure = figure.spline.getChildren()[0];
                        if (mainFigure) {
                          mainFigure.fill(figure.color);
                          mainFigure.stroke(figure.color);
                        }
                      }
                    });
                  });
              }
            }
          }
        }
      });
    });
    vueApp.$on("visibilityChanged", (target) => {
      console.log(`visibilityChanged dicom manager`, target);
      _appArr.forEach((app) => {
        setSliceIndex(app, app.current - 1);
      });
    });
    vueApp.$on("bonesTypeChanged", (value) => {
      console.log(`bonesTypeChanged dicom manager`, value);
      _appArr.forEach((app) => {
        setSliceIndex(app, app.current - 1);
      });
    });

    vueApp.$on("toothAdded", (tooth) => {
      console.log(`toothAdded handler`, tooth);
      _appArr.forEach((rootApp) => {
        if (rootApp.annotations) {
          const annotation = createAnnotationFromModel(tooth, rootApp);
          rootApp.annotations.push(annotation);
          rootApp.allAnnotations.push(annotation);
          setSliceIndex(rootApp, rootApp.current - 1);

          const apanel = document.getElementById(rootApp.apanelId);
          const annotationInstance = new Vue({
            ...DicomAnnotation,
            propsData: {
              annotation: annotation,
              app: rootApp,
              dicomBox: _dicomBox,
            },
          });
          const vueContainer = document.createElement("div");
          apanel.appendChild(vueContainer);
          annotationInstance.$mount(vueContainer);

          rootApp.childApps.forEach((app) => {
            let item = structuredClone(annotation);
            item.appIndex = app.index;
            item.parentAnnotation = annotation;
            if (!annotation.childAnnotations) {
              annotation.childAnnotations = [];
            }
            annotation.childAnnotations.push(item);
            app.annotations.push(item);

            setSliceIndex(app, app.current - 1);
          });
        } else {
          setSliceIndex(rootApp, rootApp.current - 1);
        }
      });
    });
    vueApp.$on("toothRemoved", (removedTeeth) => {
      console.log(`toothRemoved handler`, removedTeeth);
      _appArr.forEach((rootApp) => {
        if (rootApp.annotations) {
          removedTeeth.forEach((tooth) => {
            let annotation = rootApp.annotations.find(
              (x) => x.filename == tooth.filename,
            );
            if (annotation) {
              deleteAnnotation(rootApp, annotation);

              annotation.childAnnotations.forEach((annotation) => {
                let app = rootApp.childApps.find(
                  (c) => c.index == annotation.appIndex,
                );
                deleteAnnotation(app, annotation);
              });
            }
          });

          setSliceIndex(rootApp, rootApp.current - 1);
          rootApp.childApps.forEach((app) => {
            setSliceIndex(app, app.current - 1);
          });
        } else {
          setSliceIndex(rootApp, rootApp.current - 1);
        }
      });
    });

    vueApp.$on("positionChanged", (target) => {
      console.log(`positionChanged`, target);
      if (target && target.isTooth) {
        target.needUpdateDicom = true;
      }
      _appArr.forEach((app) => {
        setSliceIndex(app, app.current - 1);
      });
    });

    vueApp.$on("isDicom3DViewChanged", (isDicom3DView) => {
      console.log(`isDicom3DViewChanged`, isDicom3DView);

      _appArr.forEach((app) => {
        app.isDicom3DView = isDicom3DView;
      });
    });

    // let zipPath = `${vueApp.dicomUrl}/dicom_middle.zip`; // "demo/dicom.zip"; // `${vueApp.dicomUrl}/dicom.zip`;
    showLoaderPopup(`Preparing data`, `Please wait...`, true);
    if (!vueApp.dicomZipUrl) await loadDicomUrls();

    let zipData = await loadDicomZip(vueApp.dicomZipUrl, true);
    processDicomImage(zipData);

    // let headResponse = await fetch(zipPath, { method: "HEAD" });
    // if (headResponse.status === 200) {
    //   loadDicomZip(zipPath);
    // } else {
    //   showLoaderPopup(`Data load error`, `Please contact support@cephx.com`, true);
    // }
  }
}

async function loadDicomUrls() {
  try {
    const response = await fetch(
      `${vueApp.viewerServerUrl}/api/dicom/getDicomZipUrls?s3Path=${vueApp.dicomUrl}`,
      { cache: "no-cache" },
    );
    const json = await response.json();
    console.log("loadDicomUrls response", json);
    vueApp.dicomLowLink = json.qualityZips[0];
    if (json.qualityZips.length > 1) {
      vueApp.dicomMiddleLink = json.qualityZips[1];
    }
    if (json.qualityZips.length > 2) {
      vueApp.dicomHighLink = json.qualityZips[2];
    }
    let isMobileOrTablet = mobileAndTabletCheck();
    let isMiddleQuality = vueApp.dicomMiddleLink && !isMobileOrTablet;
    if (!vueApp.isOnlyDicom && isMiddleQuality) {
      store.commit(mutationTypes.isMiddleQualityChange, true);
      vueApp.dicomZipUrl = vueApp.dicomMiddleLink;
    } else {
      store.commit(mutationTypes.isLowQualityChange, true);
      vueApp.dicomZipUrl = vueApp.dicomLowLink;
    }
  } catch (error) {
    console.log(`loadDicomUrls error: ${error}`);
  }

  if (!vueApp.dicomZipUrl) {
    if (vueApp.dicomZipFromJson)
      vueApp.dicomZipUrl = vueApp.dicomLowLink = vueApp.dicomZipFromJson;
    else {
      vueApp.dicomZipUrl = vueApp.dicomLowLink = `${vueApp.dicomUrl}/dicom.zip`;
    }
  }
}

async function loadDicomZip(zipUrl, showPopup) {
  let zipData = null;
  try {
    zipData = vueApp.dicomData[zipUrl];
    if (!zipData) {
      if (showPopup) showLoaderPopup(`0 %`, `Download DICOM files...`, true);
      let zipPromise = new JSZip.external.Promise(function (resolve, reject) {
        let firstProgress = true;
        JSZipUtils.getBinaryContent(zipUrl, {
          progress: function (event) {
            if (firstProgress) {
              $("#loaderText").css("margin-left", "10px");
              firstProgress = false;
            }
            var percent = Math.ceil(event.percent * 0.99);
            if (showPopup) $("#loaderText").html(`${percent} %`);
          },
          callback: function (err, zipData) {
            if (err) {
              reject(err);
            } else {
              resolve(zipData);
            }
          },
        });
      });
      zipData = await zipPromise;
      console.log(`dicom zip downloaded from ${zipUrl}`);
      vueApp.dicomData[zipUrl] = zipData;
      //   readDicomSegmentation(zipUrl);
    }
  } catch (e) {
    console.log("loadDicomZip error", e);
    // showLoaderPopup(`DICOM data error`, `Please contact support@cephx.com`, true);
  }
  $id("loader").style.display = "none";
  return zipData;
}

function processDicomImage(zipData) {
  $id("loader").style.display = "block";
  $("#loaderText").html(`Loading...`);
  $("#loaderText2").html(` Just a few more seconds `);

  _appArr[0].loadImageObject([
    { name: "dicom", filename: "dicom.zip", data: zipData },
  ]);

  processDicomZipData(zipData);
}

function showLoaderPopup(text1, text2, isGray) {
  $id("loader").style.display = "block";
  $id("loader").style.background = !vueApp.isWhite && isGray ? "gray" : "white";
  $id("loader").style.opacity = "0.7";
  // $id("loader").style.color = "#007bff";

  $("#loaderText").html(text1);
  $("#loaderText2").html(text2);
  $("#loaderText3").html(``);
}

function renderDicomViews(indexes) {
  if (indexes) {
    _appArr.forEach((app) => {
      app.visible = indexes.includes(app.index);
      refreshDicomView(app);
      updateAppToRotations(app);
    });
  }

  if (viewer) viewer.controls.keysEnabled = !indexes || !indexes.length;
}

function refreshDicomView(app) {
  if (app.frameMesh && viewer) {
    viewer.scene.remove(app.frameMesh);
  }

  if (app.visible) {
    // if (!app.rendered) {
    let currentConfigs = app.getDataViewConfigs();
    app.setDataViewConfigs(currentConfigs);
    let lastKey = app.getLastKey();
    if (!lastKey) {
      return;
    }
    app.render(lastKey);
    // for (let i = 0; i < app.getNumberOfLoadedData(); ++i) {
    //   app.render(i);
    // }
    setupCanvasEvents(app);
    app.rendered = true;
    // }
    app.onResize();
    app.resetZoom();
    updateCanvasContext(app);
    update3DFrame(app);
  }
}

function setupCanvasEvents(app) {
  if (app.geometry && !app.imageOrientation) {
    try {
      let geomOrientation = app.geometry.getOrientation();
      if (geomOrientation) {
        let matrix4 = geomOrientation.getMatrix4();
        if (!matrix4.equals(identityMatrix)) {
          const dicomEuler = new THREE.Euler();
          dicomEuler.setFromRotationMatrix(matrix4, "XYZ");
          const stlEuler = new THREE.Euler(
            dicomEuler.x,
            dicomEuler.z,
            dicomEuler.y,
            "XYZ",
          );

          // console.log("app.imageOrientation", geomOrientation);
          // app.imageOrientation = new THREE.Matrix4().makeRotationFromEuler(
          //   stlEuler
          // );
          // app.inverseImageOrientation = app.imageOrientation.clone().invert();
        }
      }
    } catch (e) {
      console.error("app.imageOrientation", e);
    }
  }

  let canvasDiv = $(`#${app.divId}`)[0];
  if (canvasDiv) {
    const names = [
      // "mousedown",
      // "mousemove",
      // "mouseup",
      // "mouseout",
      // "dblclick",
      // "touchstart",
      // "touchmove",
      // "touchend",
      // "wheel",
    ];
    // canvasDiv.addEventListener(
    //   "wheel",
    //   function (event) {
    //     if (!event.shiftKey) {
    //       event.stopImmediatePropagation();
    //       let newIndex = event.deltaY < 0 ? app.current + 1 : app.current - 1;
    //       if (event.deltaY != 0 && newIndex >= 1 && newIndex <= app.max)
    //         setSliceIndex(app, newIndex - 1);
    //     }
    //   },
    //   true
    // );

    canvasDiv.addEventListener("mouseup", function (event) {
      if (app.offsetchanged) {
        // console.log("canvasDiv mouseup");
        app.offsetchanged = false;
        // setTimeout(function () {
        updateCanvasContext(app);
        // }, 300);
      }
    });
  }
}

function onKeyDown(event) {
  let app = _appArr.find((a) => a.visible);
  if (app) {
    if (event.code == "ArrowUp" || event.code == "ArrowDown") {
      event.stopImmediatePropagation();
      event.preventDefault();
      let newIndex =
        event.code == "ArrowUp" ? app.current + 1 : app.current - 1;
      if (newIndex >= 1 && newIndex <= app.max)
        setSliceIndex(app, newIndex - 1);
      // console.log("onKeyDown", event);
    } else if (event.code == "Escape") {
      _menuToothNode.style.display =
        _menuAddNode.style.display =
        _nerveAnchorMenu.style.display =
          "none";
    }
  }
}

function setSliceIndex(app, sliceIndex) {
  // console.log("setSliceIndex", sliceIndex, app);
  if (app && sliceIndex >= 0) {
    const layerGroup = app.getLayerGroupByDivId(app.divId);
    if (layerGroup) {
      const activeLayer = layerGroup.getActiveViewLayer();
      const viewController = activeLayer
        ? activeLayer.getViewController()
        : null;
      if (viewController) {
        const currentIndex = viewController.getCurrentIndex();
        const values = currentIndex.getValues();
        values[app.viewIndex] = sliceIndex > 0 ? 0 : 1;
        viewController.setCurrentIndex(new dwv.Index(values), true);
        values[app.viewIndex] = sliceIndex;
        viewController.setCurrentIndex(new dwv.Index(values));
      }
    }
  }
}

function attachAppEvents(app, dicomViewer) {
  if (app) {
    app.viewer = dicomViewer;
    app.addEventListener("positionchange", (event) => {
      const layerGroup = app.getLayerGroupByDivId(app.divId);
      const viewLayer = layerGroup.getActiveViewLayer();
      const viewController = viewLayer.getViewController();
      let newIndex = viewController.getCurrentIndex().get(app.viewIndex);
      let newCurrent = newIndex + 1;

      if (app.current != newCurrent) {
        app.current = newCurrent;
        let updatedViewer = app.viewer;
        if (app.isAnnotating && app.index != 0) {
          updatedViewer = _appArr[0].viewer;
        }

        updatedViewer.updateCurrentIndex(newCurrent);

        updateAppToRotations(app);
        update3DFrame(app);
        updateNeighborIntersections(app);
      }

      updateCanvasContext(app);
    });

    app.addEventListener("positionclick", (event) => {
      const posArr = event.value[1];
      const dwvPosition = new THREE.Vector3(posArr[0], posArr[1], posArr[2]);
      const geometry = app.geometry;
      const origin = geometry.getOrigin();
      const dwvOrigin = new THREE.Vector3(
        origin.getX(),
        origin.getY(),
        origin.getZ(),
      );

      // const dwvPoint1 = new dwv.Point3D(300, 300, 300);
      // const worldPoint1 = geometry.pointToWorld(dwvPoint1);
      // const worldPoint2 = new dwv.Point([180, 180, 180.6]);
      // const dwvPoint2 = geometry.worldToPoint(worldPoint2);

      // const vPosition = convertDwvPosition(dwvPosition, dwvOrigin);
      // console.log("dicom positionclick", event, dwvPosition, vPosition);
      // if (vPosition) {
      //   drawPoint(vPosition);
      // }
    });

    app.addEventListener("offsetchange", (event) => {
      if (!app.offset) {
        app.offsetchanged = true;
      } else {
        for (let i = 0; i < event.value.length; i++) {
          if (event.value[i] != app.offset[i]) {
            app.offsetchanged = true;
          }
        }
      }

      if (app.offsetchanged) {
        app.offset = event.value;
        updateSliceIntersection(app);
        // console.log(`app ${app.index} offsetchange`, event);
      }
    });
    app.addEventListener("zoomchange", (event) => {
      if (!app.zoomValue || app.zoomValue != event.value) {
        // console.log(`app ${app.index} zoomchange`, event, app);
        app.zoomValue = event.value;
        updateSliceIntersection(app);
        if (app.showSegmentation)
          setTimeout(function () {
            setSliceIndex(app, app.current - 1);
            // updateCanvasContext(app);
          }, 50);
      }
    });
  }
}

function convertDwvPosition(dwvPosition, dwvOrigin) {
  if (_dicomBox) {
    if (!dwvPosition.x) {
      dwvPosition.x = dwvPosition.get(0);
      dwvPosition.y = dwvPosition.get(1);
      dwvPosition.z = dwvPosition.get(2);
      // dwvPosition.get(1),
      // dwvPosition.get(2),
      // );
    }

    const vPosition = new THREE.Vector3(0, 0, 0);
    vPosition.setX(_dicomBox.min.x + dwvPosition.x - dwvOrigin.x);
    vPosition.setY(_dicomBox.min.y + dwvPosition.z - dwvOrigin.z);
    vPosition.setZ(_dicomBox.max.z - dwvPosition.y + dwvOrigin.y);

    vPosition.dicomPos = dwvPosition;
    vPosition.planePos = dwvPosition.planePos;
    return vPosition;
  }
}

function updateCanvasContext(app) {
  if (app.baseSize) {
    app.offsetchanged = false;
    destroyOutlines(app);
    destroyAnnotationShapes(app);
    if (app.isAnnotating || (app.parentApp && app.parentApp.isAnnotating)) {
      // if (app.parentApp) {
      //   console.log("child updateCanvasContext", app.index);
      // }
      drawAnnotations(app, _dicomBox);
    } else if (app.segmentations) {
      // && app.showSegmentation) {
      drawOutlines(app, _dicomBox); //, app.current, false, false);
    }
  }
}

function update3DFrame(app) {
  if (!viewer) return;
  if (app.frameMesh) {
    viewer.scene.remove(app.frameMesh);
  }
  if (
    _dicomBox &&
    !app.isAnnotating &&
    app.visible &&
    app.show3DLayer &&
    (app.show3DSlice || app.show3DBorder || app.show3DFiller)
  ) {
    let offset = (app.current - 1) / app.max; //(app.max - 1);
    if (app.index == 2) {
      offset = (app.max - app.current + 1) / app.max; //(app.max - 1);
    }
    let texture;
    if (app.baseSize && app.show3DSlice) {
      let width = app.baseSize.x;
      let height = app.baseSize.y;
      const layerGroup = app.getLayerGroupByDivId(app.divId);
      const layer = layerGroup.getActiveViewLayer();
      const imageData = layer.getImageData(); //ctx.getImageData(0, 0, width, height);
      texture = new THREE.DataTexture(imageData, width, height);
      texture.repeat.x = app.index === 0 ? 1 : app.index === 1 ? -1 : 1;
      texture.repeat.y = app.index === 0 ? -1 : app.index === 1 ? -1 : 1;
      // if (app.index != 1) {
      //     texture.flipY = true;
      //     texture.repeat.set(1, 1);
      // } else {
      //     texture.repeat.set(-1, 1);
      // }
      texture.needsUpdate = true;
    }

    app.frameMesh = drawRectangle(
      get3dSlicePoints(app.index, offset, app),
      app.color,
      app.vFrom,
      app.show3DBorder,
      app.show3DFiller,
      texture,
      app.sliceOpacity,
    );
    // app.setWindowLevelPreset("bone");
    // app.setColourMap("rainbow");

    if (viewer.scene.faceMesh) {
      viewer.scene.remove(viewer.scene.faceMesh);
      viewer.scene.add(viewer.scene.faceMesh);
    }
  }
}

function get3dSlicePoints(index, offset, app) {
  let retval = null;
  if (index === 0) {
    let y = _dicomBox.min.y;
    if (offset) {
      y += _dicomBox.size.y * offset;
    }
    retval = [
      new THREE.Vector3(_dicomBox.min.x, y, _dicomBox.min.z),
      new THREE.Vector3(_dicomBox.max.x, y, _dicomBox.min.z),
      new THREE.Vector3(_dicomBox.max.x, y, _dicomBox.max.z),
      new THREE.Vector3(_dicomBox.min.x, y, _dicomBox.max.z),
    ];
  }

  if (index === 1) {
    let x = _dicomBox.min.x;
    if (offset) {
      x += _dicomBox.size.x * offset;
    }

    retval = [
      new THREE.Vector3(x, _dicomBox.min.y, _dicomBox.min.z),
      new THREE.Vector3(x, _dicomBox.max.y, _dicomBox.min.z),
      new THREE.Vector3(x, _dicomBox.max.y, _dicomBox.max.z),
      new THREE.Vector3(x, _dicomBox.min.y, _dicomBox.max.z),
    ];
  }

  if (index === 2) {
    let z = _dicomBox.min.z;
    if (offset) {
      z += _dicomBox.size.z * offset;
    }

    retval = [
      new THREE.Vector3(_dicomBox.min.x, _dicomBox.min.y, z),
      new THREE.Vector3(_dicomBox.max.x, _dicomBox.min.y, z),
      new THREE.Vector3(_dicomBox.max.x, _dicomBox.max.y, z),
      new THREE.Vector3(_dicomBox.min.x, _dicomBox.max.y, z),
    ];
  }

  if (
    app &&
    !app.isAnnotating &&
    _appArr.includes(app) &&
    _dicomBox &&
    globalRotationMatrix
  ) {
    // let box = new THREE.Box3(retval[0], retval[2]);
    // let center = new THREE.Vector3();
    // center = box.getCenter(center);

    let center = getGlobalCenter();

    if (x3dRotation && app.index !== 1) {
      let rad = degreesToRadians(x3dRotation);
      const axis = new THREE.Vector3(1, 0, 0); // x-axis
      const quaternion = new THREE.Quaternion();
      quaternion.setFromAxisAngle(axis, rad);

      for (const vector of retval) {
        vector.sub(center);
        vector.applyQuaternion(quaternion);
        vector.add(center);
        // vector.applyAxisAngle(rotateAxis, lrRad);
      }
    }

    if (z3dRotation && app.index !== 2) {
      let rad = degreesToRadians(z3dRotation);
      const axis = new THREE.Vector3(0, 0, 1); // z-axis
      const quaternion = new THREE.Quaternion();
      quaternion.setFromAxisAngle(axis, rad);

      for (const vector of retval) {
        vector.sub(center);
        vector.applyQuaternion(quaternion);
        vector.add(center);
      }
    }

    if (y3dRotation && app.index !== 0) {
      let rad = degreesToRadians(y3dRotation);
      const axis = new THREE.Vector3(0, 1, 0); // y-axis
      const quaternion = new THREE.Quaternion();
      quaternion.setFromAxisAngle(axis, rad);

      for (const vector of retval) {
        vector.sub(center);
        vector.applyQuaternion(quaternion);
        vector.add(center);
      }
    }
  }

  return retval;
}

function getGlobalCenter() {
  let globalCenter = _dicomBox.center;
  let app0 = _appArr[0];
  let app1 = _appArr[1];
  let app2 = _appArr[2];

  let offset0 = (app0.current - 1) / app0.max;
  let offset1 = (app1.current - 1) / app1.max;
  let offset2 = (app2.max - app2.current + 1) / app2.max;

  let y = _dicomBox.min.y;
  y += _dicomBox.size.y * offset0;

  let x = _dicomBox.min.x;
  x += _dicomBox.size.x * offset1;

  let z = _dicomBox.min.z;
  z += _dicomBox.size.z * offset2;
  // globalCenter = { x, y, z };
  globalCenter = new THREE.Vector3(x, y, z);
  return globalCenter;
}

function updateOriginBox(app, viewController) {
  console.log("updateOriginBox", app.index, app.worldSize);
  let p1 = viewController.getPositionFromPlanePoint(0, 0);
  let p2 = viewController.getPositionFromPlanePoint(
    app.baseSize.x,
    app.baseSize.y,
  );
  let dz = Math.abs(p2.get(2) - p1.get(2));
  console.log(`app ${app.index} points`, p1, p2, dz);
  if (app.index == 0 && !_dicomBox) {
    let image = app.getLastImage(); //getImage(0);
    if (!image) {
      const layerGroup = app.getLayerGroupByDivId(app.divId);
      const activeLayer = layerGroup.getActiveViewLayer();
      const viewController = activeLayer.getViewController();
      image = viewController.getViewImage();
    }

    let origins = image.getGeometry().getOrigins();
    let firstOrigin = origins[0];
    let lastOrigin = origins[origins.length - 1];
    console.log(`app ${app.index} origins`, firstOrigin, lastOrigin);

    // let minX = -p2.get(0);
    // let maxX = -p1.get(0);
    // let minY = -p2.get(1);
    // let maxY = -p1.get(1);
    let minX = -(firstOrigin.getX() + app.worldSize.x);
    let maxX = -lastOrigin.getX();

    let dy = Math.abs(firstOrigin.getY() - lastOrigin.getY());
    let minY = -(firstOrigin.getY() + app.worldSize.y);
    let maxY = -lastOrigin.getY();

    let minZ = firstOrigin.getZ();
    let maxZ = lastOrigin.getZ() + app.spacing[2];

    let sizeX = maxX - minX;
    let sizeY = maxY - minY;
    let sizeZ = maxZ - minZ;

    let xOffset = -defaultXOffset;
    let yOffset = -defaultYOffset;
    let zOffset = -defaultZOffset;

    var size = { x: sizeX, y: sizeY, z: sizeZ };
    var center = {
      x: minX + sizeX / 2,
      y: minY + sizeY / 2,
      z: minZ + sizeZ / 2,
    };

    var min = { x: minX, y: minY, z: minZ };
    var max = { x: maxX, y: maxY, z: maxZ };

    _originBox = { min, max, center, size };
    console.log("_originBox", _originBox);
    // drawBox(_originBox, 0xff0000);

    _dicomBox = rotateBox(_originBox, xOffset, yOffset, zOffset);

    if (dy != 0) {
      console.log("updateOriginBox dy", dy);
      let minDZ = {
        x: _dicomBox.min.x,
        y: _dicomBox.min.y,
        z: _dicomBox.min.z + dy,
      };
      let maxDZ = {
        x: _dicomBox.max.x,
        y: _dicomBox.max.y,
        z: _dicomBox.max.z,
      };
      updateBox(_dicomBox, minDZ, maxDZ);
    }
    // drawBox(_dicomBox, 0x0000ff);
    console.log("_dicomBox", _dicomBox);
  } else if (app.worldSize.y != _dicomBox.size.y) {
    let dy = app.worldSize.y - _dicomBox.size.y;
    console.log("!!! dicom dif Y size", dy, app.worldSize, _dicomBox.size);
    // let minDY = {
    //   x: _dicomBox.min.x,
    //   y: _dicomBox.min.y, // - dy / 2,
    //   z: _dicomBox.min.z,
    // };
    // let maxDY = {
    //   x: _dicomBox.max.x,
    //   y: _dicomBox.max.y + dy,
    //   z: _dicomBox.max.z,
    // };
    // updateBox(_dicomBox, minDY, maxDY);
    // _dicomBox = convertToBox({ min: minDY, max: maxDY });
    // console.log("_dicomBox", _dicomBox);
  }

  app.dicomBox = _dicomBox;
}

function updateBox(baseBox, newMin, newMax) {
  const minX = newMin.x;
  const maxX = newMax.x;
  const minY = newMin.y;
  const maxY = newMax.y;
  const minZ = newMin.z;
  const maxZ = newMax.z;

  const sizeX = maxX - minX;
  const sizeY = maxY - minY;
  const sizeZ = maxZ - minZ;

  baseBox.min = new THREE.Vector3(minX, minY, minZ);
  baseBox.max = new THREE.Vector3(maxX, maxY, maxZ);
  baseBox.size = new THREE.Vector3(sizeX, sizeY, sizeZ);
  baseBox.center = new THREE.Vector3(
    minX + sizeX / 2,
    minY + sizeY / 2,
    minZ + sizeZ / 2,
  );

  return baseBox;
}

const delay = (ms) => new Promise((res) => setTimeout(res, ms));
const updateSegmentation = async (app, force) => {
  app.isLoading = true;
  app.text1 = "Slice segmentation";
  for (let i = 1; i <= app.max; i++) {
    app.text2 = `${i}/${app.max}`;
    await delay(10);
    drawOutlines(app, _dicomBox, i, true, force);
  }
  app.isLoading = false;

  try {
    // sendDicomSegmentation();
    console.log("sendDicomSegmentation success");
  } catch (e) {
    console.log("sendDicomSegmentation error", e);
  }
};

function changeAppZoom(app, factor) {
  if (app) {
    const element = document.getElementById(app.divId);
    if (element) {
      const rect = element.getBoundingClientRect();
      const layerGroup = app.getActiveLayerGroup();
      const viewLayer = layerGroup.getActiveViewLayer();
      const viewController = viewLayer.getViewController();

      const planePos = viewLayer.displayToMainPlanePos(
        rect.width / 2,
        rect.height / 2,
      );
      const center = viewController.getPlanePositionFromPlanePoint(planePos);

      let step = factor > 0 ? 0.1 : -0.1;
      layerGroup.addScale(step, center);
      layerGroup.draw();
    }
  }
}

function resetAppDivTransforms(app) {
  if (!app) return;
  let appDiv = $(`#${app.divId}`);
  if (appDiv) {
    let angle = 0;
    let scaleX = 1;
    let scaleY = 1;

    app.isFlippedX = scaleX < 0;
    app.isFlippedY = scaleY < 0;
    app.rotateDeg = angle;
    app.isTransformed = false;
    //app.isFlippedX || app.isFlippedY || angle !== 0;

    appDiv.css({
      "-webkit-transform": `rotate(${angle}deg) scaleX(${scaleX}) scaleY(${scaleY})`,
      "-moz-transform": `rotate(${angle}deg) scaleX(${scaleX}) scaleY(${scaleY})`,
      transform: `rotate(${angle}deg) scaleX(${scaleX}) scaleY(${scaleY})`,
    });
  }
}

// !!! changeAppTool
function changeAppTool(app, toolName, withBones) {
  if (!app) {
    app = _appArr[0];
    // if (app.toolName == toolName) return; // TODO

    if (toolName == "annotation") {
      app.toolName = toolName;
      _annotationApp = app;
      resetAppDivTransforms(app);
      app.resetZoom();
      updateCanvasContext(app);
      $(`#${app.settingsPanelId}`).collapse("hide");

      for (const xapp of _appArr) {
        xapp.visible = xapp == app;
      }
    }
  }

  if (app && toolName) {
    if (toolName == "annotation") {
      let needUpdate = !app.isAnnotating;
      app.setTool("Annotation");
      enterAnnotationTool(app, withBones);
      app.setTool("Annotation");
      if (needUpdate) {
        setTimeout(function () {
          app.setTool("Annotation");
        }, 300);
      }
    } else {
      // updateAppLayout();
      if (toolName == "dragDrop") {
        app.setTool("ZoomAndPan");
      } else if (toolName == "measure") {
        app.setTool("Draw");
        app.setToolFeatures({ shapeName: "Ruler" });
      } else if (toolName == "angle") {
        app.setTool("Draw");
        app.setToolFeatures({ shapeName: "Protractor" });
      }

      let isMeasure = toolName === "measure" || toolName === "angle";
      switchOutlineListening(app, !isMeasure);
    }
    app.toolName = toolName;
  }
}

function enterAnnotationTool(rootApp, withBones) {
  vueApp.isAnnotationMode = true;
  switchStitching("cbct");
  if (!rootApp.isAnnotating) {
    resetProjections(rootApp);
    const layerGroup = rootApp.getLayerGroupByDivId(rootApp.divId);
    const drawLayer = layerGroup.getActiveDrawLayer();
    drawLayer.deleteDraws(() => {});
    const viewLayer = layerGroup.getActiveViewLayer();
    viewLayer.noBackground = false;
    // refreshDicomView(rootApp);
    // setTimeout(function () { }, 50);
    // app.setTool("Annotation");

    $(rootApp.annotationSpinnerId).css({ display: "block" });
    setTimeout(async function () {
      if (!rootApp.annotations) {
        let nonteethAnnotations = [];
        vueApp.bones
          .concat(vueApp.nerves.filter((n) => n.isSingle))
          .concat(vueApp.gingivae)
          .concat(vueApp.airways)
          .forEach((item) => {
            const annotation = {
              name: item.displayName
                ? item.displayName
                : item.name.toLowerCase(),
              filename: item.filename,
              color: vueApp.getActualColor(item),
              isInitiliased: false,
              isVisible: item.isSingle || item.isVisible,
              isTooth: false,
              isBone: vueApp.bones.includes(item),
              isNerve: item.isNerve,
              zIndex: 0,
              fillSegmentations: true,
              isEdited: false,
              isHovered: false,
              isClicked: false,
              isFocused: false,
              hasFigures: true,
              positionChanged: false,
              changed: false,
              appIndex: rootApp.index,
              model: item,
            };
            nonteethAnnotations.push(annotation);
          });

        rootApp.annotations = [];
        vueApp.allTeeth.forEach((item, index) => {
          const annotation = createAnnotationFromModel(item, rootApp);
          annotation.zIndex = 1;
          rootApp.annotations.push(annotation);
        });
        rootApp.allAnnotations = nonteethAnnotations.concat(
          rootApp.annotations,
        );
        const apanel = document.getElementById(rootApp.apanelId);
        rootApp.allAnnotations.forEach((annotation, index) => {
          annotation.opacityColor = hexToCanvasColor(annotation.color, 0.7);
          const annotationInstance = new Vue({
            ...DicomAnnotation,
            propsData: {
              annotation: annotation,
              app: rootApp,
              dicomBox: _dicomBox,
            },
          });
          const vueContainer = document.createElement("div");
          apanel.appendChild(vueContainer);
          annotationInstance.$mount(vueContainer);
        });

        setAnnotaionContextMenuHandlers(rootApp);
      }

      if (!clonedAnnotations) {
        clonedAnnotations = [];
        vueApp.allTeeth.forEach((tooth, index) => {
          const annotation = createAnnotationFromModel(tooth, rootApp);
          clonedAnnotations.push(annotation);
        });

        initChildApps(rootApp);
        for (const app of rootApp.childApps) {
          app.annotations = structuredClone(clonedAnnotations);
          app.annotations.forEach((item) => {
            item.appIndex = app.index;
            let parentAnnotation = rootApp.annotations.find(
              (element) => element.filename == item.filename,
            );
            if (parentAnnotation) {
              item.parentAnnotation = parentAnnotation;
              if (!parentAnnotation.childAnnotations) {
                parentAnnotation.childAnnotations = [];
              }
              parentAnnotation.childAnnotations.push(item);
            }
          });
        }
      }

      calculateAllFigures(rootApp, _dicomBox);

      // await loadNrrdNerves();
      if (withBones && !_boneAnnotationsInitialized) {
        await loadNrrdBones(rootApp);
        _boneAnnotationsInitialized = true;
      }

      _appArr.forEach((app) => {
        if (app.frameMesh && viewer) {
          viewer.scene.remove(app.frameMesh);
        }
      });

      $(".flex-item").css({ display: "none" });
      $("#stl_menu").css({ display: "none" });
      $(".annotation-off-item").css({ display: "none" });
      $(".annotation-on-item").css({ display: "block" });
      $(`#${rootApp.containerId}`).css({
        display: "block",
        height: "calc(100% + 35px)",
      });
      setAppAndViewIsAnnotating(rootApp, true);
      rootApp.onResize();
      rootApp.resetZoom();

      if (rootApp.index > 0) {
        let currentConfigs = rootApp.getDataViewConfigs();
        rootApp.setDataViewConfigs(currentConfigs);
        for (let i = 0; i < rootApp.getNumberOfLoadedData(); ++i) {
          rootApp.render(i);
        }
        // to fix: mouse events
      }
      enterAnnotationMode(rootApp);

      if (!_annotationInitialized) {
        _annotationInitialized = true;

        for (const app of rootApp.childApps) {
          setTimeout(() => {
            if (!app.dicomInitialized) {
              let image0 = null;
              const app0 = app.parentApp;
              const layerGroup = app0.getLayerGroupByDivId(app0.divId);
              if (layerGroup) {
                const activeLayer = layerGroup.getActiveViewLayer();
                const viewController = activeLayer.getViewController();
                image0 = viewController.getViewImage();
              }
              if (!image0) {
                image0 = app.parentApp.getImage(0);
              }
              if (image0) app.addNewImage(image0.clone());

              app.dicomInitialized = true;
            }
            setAppAndViewIsAnnotating(app, true);
          }, 100);
        }
      }

      drawAnnotations(rootApp, _dicomBox);
      $(rootApp.annotationSpinnerId).css({ display: "none" });
    }, 50);
  }
}

function initChildApps(rootApp) {
  if (!rootApp.childApps && rootApp.viewersId) {
    rootApp.childApps = [];
    rootApp.crossedApps = [];

    _orientations.forEach((orientation, index) => {
      const app = new dwv.App();
      rootApp.childApps.push(app);
      rootApp.crossedApps.push(app);
      app.parentApp = rootApp;
      app.index = index;
      app.viewIndex = app.index !== 0 ? app.index - 1 : 2;
      app.color = _colors[index];
      app.vFrom = _vFroms[index];
      app.orientation = orientation;
      app.divId = `${rootApp.orientation}_child_${app.orientation}`;
      app.isLoading = false;
      app.visible = true;
      app.opacity = 0.8;
      app.sliceOpacity = 0.25;
      app.showSegmentation = true;
      app.showIntersection = true;
      app.is3D = false;
      app.isDicom3DView = false;
      app.show3DLayer = false;
      app.show3DFiller = true;
      app.segmentationType = "fillTeeth";
      app.segmentations = ["mandTeeth", "maxTeeth"];
      if (rootApp.index === app.index) {
        rootApp.syncApp = app;
        app.syncApp = rootApp;
      }

      const viewerInstance = new Vue({
        ...SimpleDicomViewer,
        propsData: {
          app: app,
          // app: { index: index, orientation: orientation },
        },
      });
      const vueContainer = document.createElement("div");
      document.getElementById(rootApp.viewersId).appendChild(vueContainer);
      viewerInstance.$mount(vueContainer);
    });

    rootApp.crossedApps.push(rootApp);
  }
}

function setAnnotaionContextMenuHandlers(app) {
  const geometry = app.geometry;
  const origin = geometry.getOrigin();
  const dwvOrigin = new THREE.Vector3(
    origin.getX(),
    origin.getY(),
    origin.getZ(),
  );

  let assignBtn = document.getElementById("annotation-assign-btn");
  assignBtn.callback = (currentApp, currentAnnotation, currentFigure) => {
    if (currentFigure && currentAnnotation) {
      console.log("annotation-assign-btn", currentFigure, currentAnnotation);

      const assignHeader = document.getElementById(currentApp.assignHeaderId);
      assignHeader.innerHTML = `Assign ${currentAnnotation.name}`;

      const assignList = document.getElementById(currentApp.assignListId);
      assignList.innerHTML = "";
      // while (assignList.firstChild) {
      //   assignList.removeChild(assignList.lastChild);
      // }
      // assignList.replaceChildren(...arrayOfNewChildren);
      let neighbours = getNeighborAnnotations(currentApp, currentAnnotation);

      neighbours.forEach((annotation) => {
        if (annotation == currentAnnotation) {
          return;
        }
        annotation.isSelected = false;
        const annotationInstance = new Vue({
          ...AssignAnnotation,
          propsData: {
            annotation: annotation,
            app: currentApp,
          },
        });
        const vueContainer = document.createElement("div");
        assignList.appendChild(vueContainer);
        annotationInstance.$mount(vueContainer);
      });

      currentApp.assignedFigure = currentFigure;
      currentApp.assignedAnnotation = currentAnnotation;
      currentApp.assignedSlice = currentApp.current;
      $(`#${currentApp.assignPanelId}`).css({
        display: "block",
      });
    }
  };

  let focusBtn = document.getElementById("tooth-focus-btn");
  focusBtn.callback = (currentApp, currentAnnotation) => {
    if (currentApp && currentAnnotation) {
      if (!currentAnnotation.isFocused)
        focusAnnotation(currentApp, currentAnnotation);
      else unfocusAnnotation(currentApp);
    }
  };

  let editBtn = document.getElementById("tooth-edit-btn");
  editBtn.callback = (currentApp, currentAnnotation) => {
    if (currentApp && currentAnnotation) {
      currentAnnotation.isEdited = true;
      editAnnotation(currentApp, currentAnnotation);
    }
  };

  let nrrdBtn = document.getElementById("save-nrrd-btn");
  nrrdBtn.callback = (currentApp, currentAnnotation, currentFigure) => {
    if (currentApp && currentAnnotation) {
      saveAnnotationNrrd(currentApp, currentAnnotation);
    }
  };

  let startNerveBtn = document.getElementById("annotation-start-nerve-btn");
  startNerveBtn.callback = (
    currentApp,
    currentAnnotation,
    currentFigure,
    currentPos,
  ) => {
    if (currentApp && currentAnnotation && currentPos) {
      const vPosition = convertDwvPosition(currentPos, dwvOrigin);
      currentAnnotation.startNervePoint = vPosition;
      updateNervePoints(currentApp, currentAnnotation);
    }
  };

  let endNerveBtn = document.getElementById("annotation-end-nerve-btn");
  endNerveBtn.callback = (
    currentApp,
    currentAnnotation,
    currentFigure,
    currentPos,
  ) => {
    if (currentApp && currentAnnotation) {
      const vPosition = convertDwvPosition(currentPos, dwvOrigin);
      currentAnnotation.endNervePoint = vPosition;
      updateNervePoints(currentApp, currentAnnotation);
    }
  };

  let middleNerveBtn = document.getElementById("annotation-middle-nerve-btn");
  middleNerveBtn.callback = (
    currentApp,
    currentAnnotation,
    currentFigure,
    currentPos,
  ) => {
    if (currentApp && currentAnnotation) {
      if (!currentAnnotation.middleNervePoints) {
        currentAnnotation.middleNervePoints = [];
      }
      const vPosition = convertDwvPosition(currentPos, dwvOrigin);
      currentAnnotation.middleNervePoints.push(vPosition);
      updateNervePoints(currentApp, currentAnnotation);
    }
  };

  let interpolateNerveBtn = document.getElementById("interpolate-nerve-btn");
  interpolateNerveBtn.callback = (currentApp, currentAnnotation) => {
    if (currentApp && currentAnnotation) {
      console.log("Interpolate Nerve", currentAnnotation);
      updateAuixiliaryMesh(currentAnnotation);
    }
  };

  // let smoothBtn = document.getElementById("tooth-smooth-btn");
  // smoothBtn.callback = (currentApp, currentAnnotation) => {
  //   if (currentApp && currentAnnotation) {
  //     console.log("smooth tooth");
  //   }
  // };
}

function setAppAndViewIsAnnotating(app, isAnnotating) {
  app.isAnnotating = isAnnotating;

  const layerGroup = app.getActiveLayerGroup();
  if (layerGroup) {
    const activeLayer = layerGroup.getActiveViewLayer();
    if (activeLayer) {
      const viewController = activeLayer.getViewController();
      const view = viewController.getView();
      view.isAnnotating = isAnnotating;
    }
  }
}

function createAnnotationFromModel(model, app) {
  const annotation = {
    number: model.number,
    name: model.filename.replace(".stl", ""),
    filename: model.filename,
    addedTag: model.addedTag,
    color: vueApp.getActualColor(model),
    isVisible: model.isVisible,
    isSelected: false,
    isTooth: model.isTooth,
    isNerve: model.isNerve,
    isEdited: false,
    isFocused: false,
    isDeleted: false,
    isHovered: false,
    isClicked: false,
    isInitiliased: true,
    changed: false,
    positionChanged: false,
    hasFigures: true,
    fillSegmentations: true,
    appIndex: app.index,
    model: model,
    smoothFactor: 1,
    // allFigures: {},
    // allAnnotationPoints: [],
  };
  annotation.zIndex = model.isTooth ? 1 : 0;
  annotation.opacityColor = hexToCanvasColor(annotation.color, 0.7);
  return annotation;
}

function getNeighborAnnotations(app, annotation) {
  let neighbors = [];
  if (app && annotation) {
    let aposition = annotation.model.defaultPosition;
    app.annotations.forEach((a) => {
      if (a !== annotation) {
        if (a.model && a.model.defaultPosition) {
          a.adistance = calculateDistance(a.model.defaultPosition, aposition);
        } else {
          a.adistance = 0;
        }
        neighbors.push(a);
      }
    });
    neighbors.sort((a, b) => a.adistance - b.adistance);
  }
  return neighbors;
}

/// !!! editAnnotation
async function editAnnotation(app, annotation) {
  if (annotation && annotation.isEdited) {
    if (app.focusedAnnotation && app.focusedAnnotation != annotation) {
      unfocusAnnotation(app);
    }

    if (annotation.isNerve) {
      // if (app.editedAnnotation != annotation) {
      //   focusAnnotation(app, annotation);
      //   // changeEditMode(app, "brush", "draw");
      // }

      annotation.isEdited = true;
      app.editedAnnotation = annotation;
      // switch to coronal
      if (_annotationApp != _appArr[2]) {
        await switchAnnotationToApp(_appArr[0], _appArr[2]);
      }
      let newEditedAnnotation = _annotationApp.annotations.find(
        (a) => a.model == app.editedAnnotation.model,
      );
      _annotationApp.editedAnnotation = newEditedAnnotation;
      focusAnnotation(_annotationApp, newEditedAnnotation);
      updateNervePoints(_annotationApp, newEditedAnnotation);
    } else if (app.editedAnnotation && app.editedAnnotation.isNerve) {
      // switch to axial
      await switchAnnotationToApp(_appArr[2], _appArr[0]);
    }

    app.editedAnnotation = annotation;
    focusAuxilaryModel(_annotationApp);
    if (annotation.figures) {
      annotation.figures.forEach((figure) => {
        if (figure.spline) {
          let children = figure.spline.getChildren();
          if (children && children.length > 1) children[1].fill("transparent");
        }
      });
    }
    if (annotation.childAnnotations) {
      annotation.childAnnotations
        .filter((a) => a.figures)
        .forEach((a) => {
          a.figures.forEach((figure) => {
            if (figure.spline) {
              let children = figure.spline.getChildren();
              if (children && children.length > 1)
                children[1].fill("transparent");
            }
          });
        });
    }

    annotation.isHovered = true;
    annotation.isClicked = true;
    _annotationApp.isEditingAnnotation = app.isEditingAnnotation = true;
    app.allAnnotations.forEach((a) => {
      if (a != annotation) {
        a.isFocused = a.isEdited = a.isHovered = false;
      }
    });
    _annotationApp.setToolFeatures({ edited: _annotationApp.editedAnnotation });
    if (!annotation.isNerve) $(`#${app.settingsPanelId}`).collapse("show");
  } else {
    if (app.editedAnnotation && app.editedAnnotation.isNerve) {
      unfocusAnnotation(app);
      // switch to axial
      await switchAnnotationToApp(_appArr[2], _appArr[0]);
    }
    let app0 = _appArr[0];
    let annotation0 = app0.editedAnnotation;
    if (annotation0) annotation0.isHovered = annotation0.isEdited = false;
    if (annotation) annotation.isHovered = false;
    _annotationApp.isEditingAnnotation = app.isEditingAnnotation = false;
    _annotationApp.editedAnnotation = app.editedAnnotation = null;
    _annotationApp.setToolFeatures({ edited: null });
    $(`#${app.settingsPanelId}`).collapse("hide");
  }
}

/// !!! switchAnnotationToApp
async function switchAnnotationToApp(oldApp, newApp) {
  if (!oldApp || !newApp || oldApp == newApp) {
    return;
  }
  oldApp.visible = false;
  newApp.visible = true;
  _annotationApp = newApp;

  $(`#${oldApp.divId}`).css({ display: "none" });
  $(`#${newApp.divId}`).css({ display: "block" });

  const newAppDiv = document.getElementById(newApp.divId);
  const parent0 = document.getElementById(_appArr[0].borderId);
  parent0.prepend(newAppDiv);

  let app0 = _appArr[0];
  app0.viewer.app = newApp;

  for (const childApp of app0.childApps) {
    childApp.parentApp = newApp;
  }

  if (newApp.index != 0 && !newApp.isAnnotating) {
    newApp.isCalculated = true;
    resetAppDivTransforms(newApp);
    resetProjections(newApp);

    newApp.childApps = app0.childApps;
    newApp.crossedApps = [...app0.childApps];
    newApp.crossedApps.push(newApp);
    // app0.childApps.parentApp

    const layerGroup = newApp.getLayerGroupByDivId(newApp.divId);
    const drawLayer = layerGroup.getActiveDrawLayer();
    drawLayer.deleteDraws(() => {});
    const viewLayer = layerGroup.getActiveViewLayer();
    viewLayer.noBackground = false;

    setAppAndViewIsAnnotating(newApp, true);

    newApp.toolName = "annotation";
    newApp.resetZoom();
    newApp.setTool("Annotation");
    updateCanvasContext(newApp);
    newApp.setTool("Annotation");

    newApp.onResize();
    newApp.resetZoom();
    newApp.resetDisplay();

    let currentConfigs = newApp.getDataViewConfigs();
    newApp.setDataViewConfigs(currentConfigs);
    for (let i = 0; i < newApp.getNumberOfLoadedData(); ++i) {
      newApp.render(i);
    }

    if (!newApp.allAnnotations || !newApp.allAnnotations.length) {
      newApp.annotations = newApp.allAnnotations = [];
      vueApp.nerves
        .filter((n) => n.isSingle)
        .forEach((model, index) => {
          const newAnnotation = createAnnotationFromModel(model, newApp);
          newAnnotation.isInitiliased = false;
          newApp.annotations.push(newAnnotation);

          let app0Annotation = app0.allAnnotations.find(
            (a) => a.model == newAnnotation.model,
          );
          if (app0Annotation) {
            app0Annotation.linkedAnnotation = newAnnotation;
            newAnnotation.linkedAnnotation = app0Annotation;
          }

          for (const childApp of newApp.childApps) {
            const item = createAnnotationFromModel(model, newApp);
            item.appIndex = childApp.index;
            let parentAnnotation = newAnnotation;
            // newApp.annotations.find(
            //   (element) => element.filename == item.filename
            // );
            childApp.annotations.push(item);
            if (parentAnnotation) {
              item.parentAnnotation = parentAnnotation;
              if (!parentAnnotation.childAnnotations) {
                parentAnnotation.childAnnotations = [];
              }
              parentAnnotation.childAnnotations.push(item);
            }
          }
        });

      calculateAllFigures(newApp, _dicomBox, true);

      for (const annotation of newApp.annotations) {
        await loadNervePoints(annotation, newApp);
      }
    }

    changeEditMode(newApp, "bezier");

    if (app0.editedAnnotation) {
      let newEditedAnnotation = newApp.annotations.find(
        (a) => a.model == app0.editedAnnotation.model,
      );
      if (newEditedAnnotation) {
        newApp.isEditingAnnotation = true;
        newApp.editedAnnotation = newEditedAnnotation;
        focusAnnotation(newApp, newEditedAnnotation);
        // changeEditMode(newApp, "brush", "draw");

        let view3dApp = newApp.childApps[2];
        view3dApp.is3D = true;
        switchAuxiliaryView(view3dApp);
      }
    } else {
      drawAnnotations(newApp, _dicomBox);
    }

    newApp.showIntersection =
      newApp.viewer.showIntersection =
      app0.showIntersection =
      app0.viewer.showIntersection =
        false;
    removeSliceIntersection(newApp);
  }

  setTimeout(function () {
    newApp.setTool("Annotation");
    if (newApp.index != 0) {
      changeEditMode(newApp, "bezier");

      updateNervePoints(_annotationApp, newApp.editedAnnotation);
    }
  }, 300);

  _appArr.forEach((app) => {
    if (app.frameMesh && viewer) {
      viewer.scene.remove(app.frameMesh);
    }
  });

  app0.viewer.updateCurrentIndex(newApp.current);
  // updateAppToRotations(newApp);
  // update3DFrame(newApp);
  updateNeighborIntersections(newApp);

  if (oldApp.index != 0) {
    const oldParent = document.getElementById(oldApp.borderId);
    oldParent.prepend(document.getElementById(oldApp.divId));
    $(`#${oldApp.divId}`).css({ display: "block" });
  }

  // closeViewer(oldApp);
  // changeAppTool(newApp, "annotation");
}

function editToothSettings(app, annotation) {
  initToothNumberControls();
  let tooth = annotation.model;
  if (tooth) {
    ToothNumberMode.changingAnnotation = annotation;
    ToothNumberMode.annotations = app.annotations;
    // ToothNumberMode.setChangingTooth(tooth);

    showToothNumberDialog(tooth);
    // $("#toothNumberDialog").modal();
  }
}

function addNewAnnotation(rootApp, newTooth) {
  let toothNumber = +newTooth.number;
  let addedTag = vueApp.generateAddedTag(toothNumber);
  let fileName = `tooth_${toothNumber}-${addedTag}`;

  let numberType = newTooth.numberType;
  if (numberType && numberType.trim() && numberType != "primary")
    fileName = `${fileName}-${numberType}`;

  let remarks = newTooth.remarks;
  if (remarks && remarks.trim()) fileName = `${fileName}-${remarks}`;

  const annotation = {
    number: toothNumber,
    numberType: numberType,
    remarks: remarks,
    isTooth: true,
    name: fileName,
    filename: `${fileName}.stl`,
    addedTag: addedTag,
    color: getRandomColor(), //"#faeb36"
    isVisible: true,
    isSelected: false,
    isHovered: true,
    isClicked: true,
    isEdited: true,
    isAdded: true,
    isDeleted: false,
    isFocused: false,
    changed: false,
    positionChanged: false,
    wasChanged: true,
    hasFigures: true,
    fillSegmentations: rootApp.annotationFillSegmentations,
    appIndex: rootApp.index,
    smoothFactor: 1,
    figures: [],
    // allFigures: {},
    // allAnnotationPoints: [],
    // childAnnotations: [],
    model: null, //{},
  };
  // annotation.allFigures[rootApp.current] = annotation.figures;

  let color = hexToCanvasColor(annotation.color, rootApp.annotationOpacity);
  annotation.opacityColor = color;

  let tooth = {
    id: vueApp.generateNewItemId(),
    number: annotation.number,
    savedNumber: annotation.number,
    newNumber: annotation.number,
    newNumberType: annotation.numberType,
    newRemarks: annotation.remarks,
    name: `${annotation.number}`,
    filename: annotation.filename,
    isVisible: true,
    isLoaded: false,
    isAdded: true,
    isFromData: false,
    isTooth: true,
    color: annotation.color,
    addedTag: addedTag,
  };
  updateToothDentition(tooth, false);
  updateDisplayNumberAndName(tooth);
  // tooth.isMaxilla = tooth.name.startsWith("1") || tooth.name.startsWith("2");
  // tooth.dentition = tooth.isMaxilla ? "Maxilla" : "Mandible";
  annotation.model = tooth;

  const apanel = document.getElementById(rootApp.apanelId);
  rootApp.annotations.push(annotation);
  rootApp.allAnnotations.push(annotation);
  const annotationInstance = new Vue({
    ...DicomAnnotation,
    propsData: {
      annotation: annotation,
      app: rootApp,
      dicomBox: _dicomBox,
    },
  });
  const vueContainer = document.createElement("div");
  apanel.appendChild(vueContainer);
  annotationInstance.$mount(vueContainer);

  rootApp.childApps.forEach((app) => {
    let item = structuredClone(annotation);
    item.appIndex = app.index;
    item.parentAnnotation = annotation;
    if (!annotation.childAnnotations) {
      annotation.childAnnotations = [];
    }
    annotation.childAnnotations.push(item);
    app.annotations.push(item);
  });

  rootApp.isEditingAnnotation = true;
  rootApp.editedAnnotation = annotation;
  rootApp.selectedAnnotation = annotation;
  rootApp.annotations.forEach((a) => {
    a.isEdited = false;
    a.isHovered = false;
  });
  unfocusAnnotation(rootApp);
  annotation.isEdited = true;

  focusAuxilaryModel(_annotationApp);

  // app.setTool("Draw");
  rootApp.setToolFeatures({ edited: annotation });
  $(`#${rootApp.settingsPanelId}`).collapse("show");
}

function deleteAnnotation(app, annotation) {
  if (app && annotation) {
    if (annotation.isEdited) {
      app.isEditingAnnotation = false;
      app.editedAnnotation = null;
      app.setToolFeatures({ edited: null });
    }
    annotation.isEdited = false;
    annotation.isDeleted = true;
    annotation.changed = false;
    annotation.positionChanged = false;
    annotation.hasFigures = false;
    let index = app.annotations.indexOf(annotation);
    app.annotations.splice(index, 1);
    if (app.allAnnotations) {
      index = app.allAnnotations.indexOf(annotation);
      app.allAnnotations.splice(index, 1);
    }
    if (annotation.figures)
      annotation.figures.forEach((figure) => {
        if (figure.spline) figure.spline.destroy();
        if (figure.anchors) {
          figure.anchors.forEach((anchor) => anchor.destroy());
        }
      });

    if (app.childApps) {
      if (!app.deletedAnnotations) {
        app.deletedAnnotations = [];
      }
      app.deletedAnnotations.push(annotation);
    }
  }
}

function updateAnnotationPosition(app, annotation) {
  if (annotation.positionChanged) {
    annotation.positionChanged = false;

    if (annotation.linkedAnnotation && app.index == 0) {
      annotation.linkedAnnotation.positionChange = {
        difX: annotation.positionChange.difX,
        difY: annotation.positionChange.difY,
        appIndex: 0,
      };
      updateAnnotationPosition(
        _appArr[annotation.linkedAnnotation.appIndex],
        annotation.linkedAnnotation,
      );
      return;
    }

    let difX = annotation.positionChange.difX;
    let difY = annotation.positionChange.difY;

    let appIndex = annotation.positionChange.appIndex;

    let dx = 0;
    let dy = 0;
    let dz = 0;
    //|| !app.parentApp
    if (appIndex == 0) {
      dx = +difX;
      dy = +difY;
    } else if (appIndex == 1) {
      dy = +difX;
      dz = +difY;
    } else if (appIndex == 2) {
      dx = +difX;
      dz = +difY;
    }

    console.log("updateAnnotationPosition", appIndex, dx, dy, dz);

    if (annotation.draggedFigure) {
      let afigure = annotation.draggedFigure;
      let apArr = annotation.allAnnotationPoints;
      afigure.annotationPoints.forEach((p) => {
        let index = apArr.indexOf(p);
        if (index >= 0) {
          apArr[index] = apArr[apArr.length - 1];
          apArr.pop();
        }
      });

      let apoints = [...afigure.annotationPoints.values()];
      afigure.annotationPoints.clear();

      apoints.forEach((point) => {
        point.x += dx;
        point.y += dy;
        point.z += dz;
        const str = `${point.x},${point.y}`;
        afigure.annotationPoints.set(str, point);
      });
      annotation.allAnnotationPoints.push(...afigure.annotationPoints.values());

      if (afigure.konvaPoints) {
        let kpoints = [...afigure.konvaPoints];
        // afigure.konvaPoints = [];
        kpoints.forEach((point) => {
          point.x += dx;
          point.y += dy;
          point.z += dz;
          // afigure.konvaPoints.push(point);
        });
      }
      annotation.draggedFigure = null;
    } else {
      let newAllFigures = {};
      annotation.allAnnotationPoints = [];
      for (var sliceIndex in annotation.allFigures) {
        let sli = +sliceIndex;
        let figures = annotation.allFigures[sliceIndex];
        if (figures) {
          let newFigures = [];
          for (const afigure of figures) {
            let apoints = [...afigure.annotationPoints.values()];
            afigure.annotationPoints.clear();

            apoints.forEach((point) => {
              point.x += dx;
              point.y += dy;
              point.z += dz;
              const str = `${point.x},${point.y}`;
              afigure.annotationPoints.set(str, point);
            });
            annotation.allAnnotationPoints.push(
              ...afigure.annotationPoints.values(),
            );
            newFigures.push(afigure);

            if (afigure.konvaPoints) {
              let kpoints = [...afigure.konvaPoints];
              // afigure.konvaPoints = [];
              kpoints.forEach((point) => {
                point.x += dx;
                point.y += dy;
                point.z += dz;
                // afigure.konvaPoints.push(point);
              });
            }
          }
          newAllFigures[sli + dz] = newFigures;
          if (!Object.hasOwn(newAllFigures, sli) && dz !== 0)
            newAllFigures[sli] = [];
        }
      }
      annotation.allFigures = newAllFigures;

      if (annotation.model && viewer) {
        let mesh = viewer.get_model_mesh(annotation.model.id);
        if (mesh) {
          let dif = get3dDif(dx, dy, dz);
          let position = mesh.position;
          let point = new THREE.Vector3(
            position.x + dif.dx,
            position.y + dif.dy,
            position.z + dif.dz,
          );
          mesh.position.copy(point);
        }
      }
    }
  }

  updateCanvasContext(app);
  if (app.childApps) {
    app.childApps.forEach((childApp) => {
      updateCanvasContext(childApp);
    });
  }
}

function get3dDif(dx, dy, dz) {
  let retval = { dx: 0, dy: 0, dz: 0 };

  if (dx != 0) {
    let appSize = _appArr[1].max;
    retval.dx = dx * (_dicomBox.size.x / appSize);
  }
  if (dy != 0) {
    let appSize = _appArr[2].max;
    retval.dz = -dy * (_dicomBox.size.z / appSize);
  }
  if (dz != 0) {
    let appSize = _appArr[0].max;
    retval.dy = dz * (_dicomBox.size.y / appSize);
  }
  return retval;
}

function updateAppLayout() {
  $("#stl_menu").css({ display: "block" });
  $("#views3d_wrapper").css({ display: "block" });

  let orientations = [];
  let indexes = [];
  if (store.state.dicom.isAxial) {
    $("#axial_cont").css({ display: "block" });
    orientations.push("axial");
    indexes.push(0);
  }
  if (store.state.dicom.isSaggital) {
    $("#sagittal_cont").css({ display: "block" });
    orientations.push("sagittal");
    indexes.push(1);
  }
  if (store.state.dicom.isCoronal) {
    $("#coronal_cont").css({ display: "block" });
    orientations.push("coronal");
    indexes.push(2);
  }
  vueApp.isDicomOpen = indexes.length > 0;
  if (vueApp.isDicomOpen) {
    if (indexes.length > 1 || vueApp.isMobileDevice) {
      $(`#teethListPanel`).removeClass("show");
    }
    vueApp.hideClipping();
    // $("#orthoMenu").css({ display: "none" });
    if (vueApp.activeMode && vueApp.activeMode.dicomIncompatible) {
      vueApp.activeMode.leaveMode();
      vueApp.activeMode = null;
    }
    switchStitching("cbct");

    if (!vueApp.isRandomTeethColor) {
      updateRandomTeethColors(false, true);
      vueApp.$emit("colorChanged", "randomTeethColor");
    }

    vueApp.bones.forEach((item) => {
      if (item.color == vueApp.bonesColor) {
        item.color = item.isMaxilla ? "#e81416" : "#487de7"; // getRandomColor();
        itemVisibilityChanged(item, item.color);
        // viewer.set_color(item.id, item.color);
      }
    });
  } else {
    vueApp.restoreClipping();
    // $("#orthoMenu").css({ display: "unset" });
    // $("#orthoMenu").removeAttr("style");
    switchStitching(vueApp.viewSourceType);

    if (vueApp.isSplitView) {
      vueApp.switchSplitView();
    }
    if (!vueApp.isOnlyDicom && vueApp.isDicom3DView) {
      vueApp.switchDicom3dView();
    }

    var savedGroups = vueApp.savedData.dentations.reduce(function (a, b) {
      return a.concat(b.groups);
    }, []);
    var savedTeeth = savedGroups.reduce(function (a, b) {
      return a.concat(b.teeth);
    }, []);

    if (!vueApp.isRandomTeethColor) {
      vueApp.allTeeth.forEach((item) => {
        var savedTooth = savedTeeth.find(function (data, index) {
          return data.filename === item.filename;
        });
        if (item.isVisible) {
          item.color = savedTooth ? savedTooth.color : vueApp.teethColor;
          itemVisibilityChanged(item, item.color);
        }
      });
    }

    vueApp.bones.forEach((item) => {
      var savedBone = vueApp.savedData.bones.find(function (data, index) {
        return data.filename === item.filename;
      });
      item.color = savedBone ? savedBone.color : vueApp.bonesColor;
      itemVisibilityChanged(item, item.color);
      // viewer.set_color(item.id, item.color);
    });
  }

  if (vueApp.isMobileDevice) {
    $(".flex-item").css({
      height: indexes.length > 0 ? "calc(50% - 2px)" : "100%",
    });
    $(".flex-item").css({
      width: "100%",
    });
  } else {
    $(".flex-item").css({
      width: indexes.length > 0 ? "calc(50% - 2px)" : "100%",
    });
    $(".flex-item").css({
      height: indexes.length > 1 ? "calc(50% - 2px)" : "100%",
    });
  }

  if (viewer) viewer.do_resize();

  // if (indexes.length > 0 && !_colorsUpdated) {
  //   _colorsUpdated = true;
  //   if (!vueApp.isRandomTeethColor) {
  //     updateRandomTeethColors(false, true);
  //   }

  //   updateSavedData();
  //   vueApp.saveData();
  // }

  // console.log(orientations);
  // setTimeout(function () {
  renderDicomViews(indexes);
  // }, 50);
  // document.getElementById("dicomSubMenu").classList.remove("show");
  // document.getElementById("viewSubMenu").classList.remove("show");
}

function hideAllDicomPanels() {
  store.commit(mutationTypes.isAxialChange, false);
  store.commit(mutationTypes.isSaggitalChange, false);
  store.commit(mutationTypes.isCoronalChange, false);

  $("#axial_cont").css({ display: "none" });
  $("#sagittal_cont").css({ display: "none" });
  $("#coronal_cont").css({ display: "none" });
}

function switchToNextApp(currentApp) {
  if (currentApp) {
    hideAllDicomPanels();

    if (currentApp.index == 0) {
      store.commit(mutationTypes.isSaggitalChange, true);
      $("#sagittal_cont").css({ display: "block" });
    }

    if (currentApp.index == 1) {
      store.commit(mutationTypes.isCoronalChange, true);
      $("#coronal_cont").css({ display: "block" });
    }

    if (currentApp.index == 2) {
      store.commit(mutationTypes.isAxialChange, true);
      $("#axial_cont").css({ display: "block" });
    }

    updateAppLayout();
  }
}

async function closeViewer(app) {
  if (app) {
    if (app.isAnnotating) {
      if (_annotationApp.index != 0) {
        let eannotation = _annotationApp.editedAnnotation;
        if (eannotation) {
          eannotation.isEdited = false;
          await editAnnotation(_annotationApp, eannotation);
        } else {
          await switchAnnotationToApp(_annotationApp, _appArr[0]);
        }
        closeViewer(_appArr[0]);
        return;
      }
      resetProjections();

      $("#stl_menu").css({ display: "block" });
      $("#views3d_wrapper").css({ display: "block" });
      vueApp.isAnnotationMode = false;
      $(".teeth-list-ctrl").css("display", "block");
      switch3DView(app, false);

      $(".annotation-off-item").css({ display: "block" });
      $(".annotation-on-item").css({ display: "none" });
      $(`.annotation-settings-panel`).collapse("hide");
      $(`.dicom-settings-panel`).collapse("hide");

      for (const xapp of _appArr) {
        setAppAndViewIsAnnotating(xapp, false);

        const layerGroup = xapp.getLayerGroupByDivId(xapp.divId);
        const viewLayer = layerGroup.getActiveViewLayer();
        viewLayer.noBackground = xapp.noBackground;

        changeAppTool(xapp, "dragDrop");
        // refreshDicomView(app);
        setSliceIndex(xapp, xapp.current - 1);
        xapp.viewer.app = xapp;
      }

      updateStl3D(app);
      leaveAnnotationMode();
      unfocusTooth();
      displayLastStitchingResult();
    } else {
      console.log("closeViewer", app.orientation);
      $(`#${app.containerId}`).css({ display: "none" });
      store.commit(mutationTypes.closeProjection, app.index);
    }
    updateAppLayout();
  }
}

function switchTeethNumbers(app) {
  app.showTeethNumbers = !app.showTeethNumbers;
  if (app.showTeethNumbers) {
    drawTeethNumbers();
    // enterToothNumberMode(true);
  } else {
    removeTeethNumbers();
    // leaveToothNumberMode(false, false);
  }
}

function switch3DView(app, is3D) {
  app.is3D = is3D;
  updateNeighborIntersections(app, app.crossedApps);
  let stlDiv = document.getElementById("views3d_cont");
  let dicomDiv = document.getElementById(app.divId);
  if (is3D) {
    $(`#${app.settingsPanelId}`).collapse("hide");

    updateStl3D(app);

    dicomDiv.style.display = "none";
    $(".3d-off-item").css("display", "none");
    let parentDiv = dicomDiv.parentNode;
    parentDiv.insertBefore(stlDiv, dicomDiv.nextSibling);
    // console.log(stlDiv.style.width);
    if (viewer) {
      viewer.do_resize();

      app.childApps.forEach((childApp) => {
        update3DFrame(childApp);
      });

      if (app.annotations) {
        let focusedAnnotation = app.annotations.find((a) => a.isFocused);
        app.allAnnotations.forEach((annotation) => {
          let mesh = viewer.get_model_mesh(annotation.model.id);
          if (!mesh || mesh.visible === undefined) {
            console.log("WRONG ANNOTATION MODEL", annotation.model);
            return;
          }
          if (focusedAnnotation) {
            mesh.visible = annotation === focusedAnnotation;
          } else {
            mesh.visible = annotation.isVisible;
          }
        });
      }
    }
  } else {
    let parentDiv = document.getElementById("views3d_wrapper");
    let firstChild = parentDiv.firstChild;
    parentDiv.insertBefore(stlDiv, firstChild);
    dicomDiv.style.display = "block";
    $(".3d-off-item").css("display", "block");
    if (viewer) {
      viewer.do_resize();

      app.childApps.forEach((childApp) => {
        if (childApp.frameMesh) {
          viewer.scene.remove(childApp.frameMesh);
        }
      });

      vueApp.allItems.forEach(function (item) {
        let color = item.color;
        if (item.isTooth) {
          color = vueApp.getActualColor(item);
        }
        itemVisibilityChanged(item, color);
      });
    }
  }
}

function updateStl3D(app) {
  if (!app || !app.annotations) return;
  if (viewer && app.deletedAnnotations) {
    app.deletedAnnotations.forEach((annotation) => {
      if (annotation.model && annotation.model.id && !annotation.wasDeleted) {
        vueApp.removeTooth(annotation.model);
        viewer.remove_model(annotation.model.id);
        annotation.wasDeleted = true;
      }
    });
    // app.deletedAnnotations = [];
  }

  let changedAnnotations = app.annotations.filter(
    (a) => a.wasChanged || a.wasSaved,
  );
  if (viewer && changedAnnotations.length) {
    changedAnnotations.forEach((annotation) => {
      if (annotation.isAdded && !annotation.created) {
        let blob = getAnnotationData(annotation, app);
        if (!blob) {
          console.log("updateStl3D empty annotation", annotation.name);
          return;
        }

        let tooth = annotation.model;
        vueApp.addTooth(tooth);
        tooth.stlModelLoaded = initAnnotationTooth;

        var stlModel = {
          local_file: new File([blob], annotation.filename),
          color: tooth.color,
        };
        stlModel.id = tooth.id;
        viewer.add_model(stlModel);
        annotation.created = true;
      } else {
        let model = annotation.model;
        let mesh = viewer.get_model_mesh(model.id);
        // if (!annotation.baseMesh) {
        //   annotation.baseMesh = mesh.clone();
        // }

        let newGeometry = getAnnotationGeometry(annotation, app);
        if (newGeometry) {
          // let vmesh = new THREE.Mesh(newGeometry);
          // let box = new THREE.Box3().setFromObject(vmesh);
          newGeometry.computeBoundingBox();
          let box = newGeometry.boundingBox;
          box = offsetBox(box);
          model.defaultBox = box;
          model.defaultPosition = {
            x: box.center.x,
            y: box.center.y,
            z: box.center.z,
          };
          mesh.position.set(box.center.x, box.center.y, box.center.z);

          let geometry = newGeometry;

          try {
            newGeometry.center();
            newGeometry.computeVertexNormals();
            geometry = newGeometry;
            // geometry = new THREE.Geometry().fromBufferGeometry(newGeometry);
            // geometry.mergeVertices();
            // geometry.computeVertexNormals();
            // geometry = new THREE.BufferGeometry().fromGeometry(geometry);
            // geometry.computeBoundingBox();
          } catch (error) {
            console.error("smooth geometry error", annotation.name, error);
          }

          if (geometry) {
            mesh.geometry.dispose();
            mesh.geometry = mesh.cbctGeometry = geometry;

            viewer.set_color(model.id, vueApp.getActualColor(model));

            if (viewer.scene.children.indexOf(mesh) < 0) {
              viewer.scene.add(mesh);
            }
            mesh.material.flatShading = false;
          }
        } else {
          viewer.scene.remove(mesh);
        }
      }
    });
  }
}

function initAnnotationTooth(model_id) {
  console.log("initAnnotationTooth", model_id);
  if (!viewer) return;
  let model = vueApp.getItemById(model_id);
  let vmesh = viewer.get_model_mesh(model.id);
  if (vmesh && vmesh.geometry) {
    let box = new THREE.Box3().setFromObject(vmesh);
    box = offsetBox(box);
    model.defaultBox = box;
    model.uuid = vmesh.uuid;
    model.defaultPosition = {
      x: box.center.x,
      y: box.center.y,
      z: box.center.z,
    };

    vmesh.geometry.center();
    vmesh.position.set(box.center.x, box.center.y, box.center.z);
    vmesh.material.flatShading = false;
    vmesh.geometry.computeVertexNormals();
    if (!vmesh.originalMaterial) vmesh.originalMaterial = vmesh.material;

    if (model.isTooth) {
      var clone_geometry = vmesh.geometry.clone();
      var clone_material = vmesh.originalMaterial.clone();
      clone_material.color.set(0xffffff);
      clone_material.opacity = 0.0001;
      clone_material.transparent = true;
      var defaultMesh = new THREE.Mesh(clone_geometry, clone_material);
      defaultMesh.visible = false;
      viewer.scene.add(defaultMesh);
      defaultMesh.position.set(box.center.x, box.center.y, box.center.z);
      model.defaultMeshId = defaultMesh.id;
      defaultMesh.isVisible = false;

      setToothMidData(model);
      var apexMesh = drawPoint(model.apex, 0x0000ff, true, 0.01);
      vmesh.add(apexMesh);
      apexMesh.position.set(
        model.apex.x - box.center.x,
        model.apex.y - box.center.y,
        model.apex.z - box.center.z,
      );
      vmesh.apexMesh = apexMesh;

      var rootMesh = drawPoint(model.root, 0xff0000, true, 0.01);
      vmesh.add(rootMesh);
      rootMesh.position.set(
        model.root.x - box.center.x,
        model.root.y - box.center.y,
        model.root.z - box.center.z,
      );
      vmesh.rootMesh = rootMesh;
    }

    vueApp.switchToDefaultView();
    // vueApp.lookAtPoint(box.center);
  }
  focusAuxilaryModel(_annotationApp);
}

function updateSliceIntersection(app, appArr, excludeVertical, hideIcons) {
  if (app && app.divId) {
    removeSliceIntersection(app);

    if (app.showIntersection) {
      if (!appArr) {
        let crossedApps = app.parentApp
          ? app.parentApp.crossedApps
          : app.crossedApps;
        appArr = app.isAnnotating && crossedApps ? crossedApps : _appArr;
      }
      const layerGroup = app.getLayerGroupByDivId(app.divId);
      if (!layerGroup) return;

      const containerId = app.divId;
      const containerDiv = document.getElementById(containerId);
      containerDiv.app = app;
      containerDiv.appArr = appArr;

      const hColor = app.orientation == "axial" ? _colors[2] : _colors[0];
      const vColor = app.orientation == "sagittal" ? _colors[2] : _colors[1];

      let left = containerDiv.offsetWidth / 2;
      let top = containerDiv.offsetHeight / 2;
      let leftSub10 = left - 10;
      let topSub10 = top - 10;

      const intersectionDivId = containerId + "-intersection";
      let intersectionDiv = document.getElementById(intersectionDivId);
      const hId = containerId + "-intersection-horizontal";
      let lineH = document.getElementById(hId);
      const vId = containerId + "-intersection-vertical";
      let lineV = document.getElementById(vId);

      const circleId = containerId + "-intersection-circle";
      let circleDiv = document.getElementById(circleId);

      if (!intersectionDiv) {
        intersectionDiv = document.createElement("div");
        intersectionDiv.id = intersectionDivId;
        intersectionDiv.className = "dicom-intersection-cont";
        intersectionDiv.app = app;
        containerDiv.appendChild(intersectionDiv);

        containerDiv.addEventListener("mouseup", projectionMouseUp);
        containerDiv.addEventListener("mouseleave ", projectionMouseUp);
        containerDiv.addEventListener("mouseout ", projectionMouseUp);
        containerDiv.addEventListener("mousemove", projectionMouseMove);
        containerDiv.addEventListener("dblclick", projectionDblClick);

        if (!lineH) {
          lineH = document.createElement("div");
          lineH.id = hId;
          lineH.isHorizontal = true;
          lineH.className = "dicom-intersection horizontal";
          intersectionDiv.appendChild(lineH);

          const lineHc = document.createElement("div");
          lineHc.className = "dicom-intersection hc";
          lineHc.style.borderTop = "1px solid " + hColor;
          lineH.appendChild(lineHc);

          let imgMoveHor1 = document.createElement("img");
          imgMoveHor1.id = containerId + "-projection-imgMoveHor1";
          imgMoveHor1.className = "projection-icon img-move-hor1";
          imgMoveHor1.src = "images/move_vertical.png";
          imgMoveHor1.addEventListener("mousedown", projectionMoveMouseDown);
          lineH.appendChild(imgMoveHor1);

          let imgMoveHor2 = document.createElement("img");
          imgMoveHor2.id = containerId + "-projection-imgMoveHor2";
          imgMoveHor2.className = "projection-icon img-move-hor2";
          imgMoveHor2.src = "images/move_vertical.png";
          imgMoveHor2.addEventListener("mousedown", projectionMoveMouseDown);
          lineH.appendChild(imgMoveHor2);

          let imgRotateHor1 = document.createElement("img");
          imgRotateHor1.id = containerId + "-projection-imgRotateHor1";
          imgRotateHor1.className =
            "projection-icon img-rotate-hor1 projection-rotate";
          imgRotateHor1.src = "images/rotate_right_white.png";
          imgRotateHor1.addEventListener(
            "mousedown",
            projectionRotateMouseDown,
          );
          lineH.appendChild(imgRotateHor1);

          let imgRotateHor2 = document.createElement("img");
          imgRotateHor2.id = containerId + "-projection-imgRotateHor2";
          imgRotateHor2.className =
            "projection-icon img-rotate-hor2 projection-rotate";
          imgRotateHor2.src = "images/rotate_right_white.png";
          imgRotateHor2.addEventListener(
            "mousedown",
            projectionRotateMouseDown,
          );
          lineH.appendChild(imgRotateHor2);
        }

        if (!lineV) {
          lineV = document.createElement("div");
          lineV.id = vId;
          lineV.isVertical = true;
          lineV.className = "dicom-intersection vertical";
          intersectionDiv.appendChild(lineV);

          const lineVc = document.createElement("div");
          lineVc.className = "dicom-intersection vc";
          lineVc.style.borderLeft = "1px solid " + vColor;
          lineV.appendChild(lineVc);

          let imgMoveVert1 = document.createElement("img");
          imgMoveVert1.id = containerId + "-projection-imgMoveVert1";
          imgMoveVert1.className = "projection-icon img-move-vert1";
          imgMoveVert1.src = "images/move_horizontal.png";
          imgMoveVert1.addEventListener("mousedown", projectionMoveMouseDown);
          lineV.appendChild(imgMoveVert1);

          let imgMoveVert2 = document.createElement("img");
          imgMoveVert2.id = containerId + "-projection-imgMoveVert2";
          imgMoveVert2.className = "projection-icon img-move-vert2";
          imgMoveVert2.src = "images/move_horizontal.png";
          imgMoveVert2.addEventListener("mousedown", projectionMoveMouseDown);
          lineV.appendChild(imgMoveVert2);

          let imgRotateVert1 = document.createElement("img");
          imgRotateVert1.id = containerId + "-projection-imgRotateVert1";
          imgRotateVert1.className =
            "projection-icon img-rotate-vert1 projection-rotate";
          imgRotateVert1.src = "images/rotate_right_white.png";
          imgRotateVert1.addEventListener(
            "mousedown",
            projectionRotateMouseDown,
          );
          lineV.appendChild(imgRotateVert1);

          let imgRotateVert2 = document.createElement("img");
          imgRotateVert2.id = containerId + "-projection-imgRotateVert2";
          imgRotateVert2.className =
            "projection-icon img-rotate-vert2 projection-rotate";
          imgRotateVert2.src = "images/rotate_right_white.png";
          imgRotateVert2.addEventListener(
            "mousedown",
            projectionRotateMouseDown,
          );
          lineV.appendChild(imgRotateVert2);
        }

        if (!circleDiv) {
          circleDiv = document.createElement("div");
          circleDiv.id = circleId;
          circleDiv.isHorizontal = circleDiv.isVertical = true;

          circleDiv.className = "dicom-intersection-circle";
          circleDiv.addEventListener("mousedown", projectionCircleMouseDown);
          intersectionDiv.appendChild(circleDiv);
        }
      }
      intersectionDiv.style.display = "block";

      lineH.app = lineV.app = circleDiv.app = app;
      lineH.appArr = lineV.appArr = circleDiv.appArr = appArr;
      lineH.containerDiv =
        lineV.containerDiv =
        circleDiv.containerDiv =
          containerDiv;

      let values = [0, 0, 0, 0];
      for (let appItem of appArr) {
        if (!appItem.current) {
          values = null;
          break;
        }
        if (!appItem.is3D || appItem.parentApp)
          values[appItem.viewIndex] = appItem.current - 1;
      }

      if (values) {
        const viewLayer = layerGroup.getActiveViewLayer();
        const viewController = viewLayer.getViewController();

        const index = new dwv.Index(values);

        let image = app.getLastImage(); //getImage(0);
        if (!image) {
          const layerGroup = app.getLayerGroupByDivId(app.divId);
          const activeLayer = layerGroup.getActiveViewLayer();
          const viewController = activeLayer.getViewController();
          image = viewController.getViewImage();
        }

        const geometry = image.getGeometry();
        const position = geometry.indexToWorld(index);

        const p2D = viewController.getPlanePositionFromPosition(position);
        const displayPos = viewLayer.planePosToDisplay(p2D.x, p2D.y);
        left = displayPos.x;
        top = displayPos.y;
        leftSub10 = left - 10;
        topSub10 = top - 10;

        if (app.transformAngle && app.transformOrigin) {
          if (
            app.isProjectionMoving &&
            projectionTarget &&
            (!projectionTarget.isVertical || !projectionTarget.isHorizontal)
          ) {
            left = app.projectionLeft;
            top = app.projectionTop;
            leftSub10 = left - 10;
            topSub10 = top - 10;
          }

          intersectionDiv.style.transformOrigin = `${left}px ${top}px`;
          app.transformOrigin = { x: left, y: top };
        }

        // let planeScale = viewLayer.displayToPlaneScale(left, top);
        // let planePos = viewLayer.displayToPlanePos(left, top);
        // let mainPlanePos = viewLayer.displayToMainPlanePos(left, top);
        // let planeIndex = viewLayer.displayToPlaneIndex(left, top);
        // planeIndex = viewLayer.displayToPlaneIndex(left, top);
      }

      app.projectionTop = top;
      app.projectionLeft = left;

      lineH.style.marginTop = top + "px";
      lineH.style.display = top < 0 ? "none" : "block";

      lineV.style.marginLeft = left + "px";
      lineV.style.display = left < 0 || excludeVertical ? "none" : "block";

      let circleHidden = top < 0 || left < 0 || excludeVertical;
      circleDiv.style.display = circleHidden ? "none" : "block";
      circleDiv.style.marginLeft = leftSub10 + "px";
      circleDiv.style.marginTop = topSub10 + "px";

      // if (app.parentApp || app.isAnnotating) hideIcons = true;
      $(`.projection-rotate`).css("display", hideIcons ? "none" : "block");
    } else {
      removeSliceIntersection(app);
    }
  }
}

function rotate2dPoint(cx, cy, x, y, angle) {
  var radians = (Math.PI / 180) * angle,
    cos = Math.cos(radians),
    sin = Math.sin(radians),
    nx = cos * (x - cx) + sin * (y - cy) + cx,
    ny = cos * (y - cy) - sin * (x - cx) + cy;
  return { x: nx, y: ny };
}

function projectionDblClick(event) {
  let containerDiv = event.currentTarget;
  let app = containerDiv.app;
  let appArr = containerDiv.appArr;

  if (app && !app.isAnnotating && !app.parentApp) {
    const layerGroup = app.getLayerGroupByDivId(app.divId);
    const viewLayer = layerGroup.getActiveViewLayer();
    const viewController = viewLayer.getViewController();

    let offsetX = event.offsetX;
    let offsetY = event.offsetY;
    let rect = containerDiv.getBoundingClientRect();
    offsetX = event.clientX - rect.left; //x position within the element.
    offsetY = event.clientY - rect.top; //y position within the element.

    // const planePos = viewLayer.displayToPlanePos(offsetX, offsetY);
    // const k = viewController.getCurrentScrollIndexValue();
    // const planePoint = new dwv.Point3D(planePos.x, planePos.y, k);
    // let rotatedPoint = viewController
    //   .getPlaneHelper()
    //   .getImageOrientedPoint3D(planePoint);
    // const view = viewController.getView();
    // const rotationMatrix = view.getRotationMatrix();
    // if (rotationMatrix && typeof rotationMatrix !== "undefined") {
    //   const geometry = viewController.getViewImage().getGeometry();
    //   const currentIndex = viewController.getCurrentIndex();
    //   rotatedPoint = viewController.getRotatedPoint(
    //     rotatedPoint,
    //     geometry,
    //     currentIndex,
    //     rotationMatrix
    //   );
    // }
    // let axialIndex = +Math.round(rotatedPoint.getZ());
    // let saggitalIndex = +Math.round(rotatedPoint.getX());
    // let coronalIndex = +Math.round(rotatedPoint.getY());
    // if (_appArr[0] != app && _appArr[0].current != axialIndex + 1)
    //   setSliceIndex(_appArr[0], axialIndex);
    // if (_appArr[1] != app && _appArr[1].current != saggitalIndex + 1)
    //   setSliceIndex(_appArr[1], saggitalIndex);
    // if (_appArr[2] != app && _appArr[2].current != coronalIndex + 1)
    //   setSliceIndex(_appArr[2], coronalIndex);

    let currentValues = [0, 0, 0, 0];
    for (let appItem of appArr) {
      if (!appItem.current) {
        currentValues = null;
        break;
      }
      if (!appItem.is3D || appItem.parentApp)
        currentValues[appItem.viewIndex] = appItem.current - 1;
    }

    if (viewLayer && currentValues) {
      let newIndex = viewLayer.displayToPlaneIndex(offsetX, offsetY);
      let tempValues = newIndex.getValues();
      let newValues = [...currentValues];
      newValues[app.viewIndex] = app.current - 1;
      if (app.viewIndex == 2) {
        newValues[0] = tempValues[0];
        newValues[1] = tempValues[1];
      } else if (app.viewIndex == 1) {
        newValues[0] = tempValues[0];
        newValues[2] = tempValues[1];
      } else {
        newValues[1] = tempValues[0];
        newValues[2] = tempValues[1];
      }

      let anyChanges = false;
      for (let i = 0; i < newValues.length; i++) {
        if (newValues[i] != currentValues[i]) {
          anyChanges = true;
          break;
        }
      }
      if (anyChanges) {
        app.afterDoubleClick = true;
        for (let appItem of appArr) {
          if (appItem != app) {
            let currentValue = appItem.current - 1;
            let newValue = newValues[appItem.viewIndex];
            if (currentValue != newValue) {
              setSliceIndex(appItem, newValue);
            }
          }
        }

        if (globalRotationMatrix) {
          let app0 = _appArr[0];
          let app1 = _appArr[1];
          let app2 = _appArr[2];

          update3DFrame(app0);
          update3DFrame(app1);
          update3DFrame(app2);
        }

        app.afterDoubleClick = false;
      }
    }
    // const geometry = viewController.getViewImage().getGeometry();
    // const point3D = geometry.pointToWorld(rotatedPoint);
    // const currentPosition = viewController.getCurrentPosition();
    // const worldPosition = currentPosition.mergeWith3D(point3D);
    // console.log("projectionDblClick", axialIndex, saggitalIndex, coronalIndex);
  }
}

function projectionMouseUp(event) {
  isProjectionMoving = isProjectionRotating = false;
  if (projectionRotateAngle !== null && projectionTarget) {
    // console.log("projectionMouseUp", event);
    let app = projectionTarget.app;
    updateIntersectionDivAngle(app);
    if (app.syncApp) {
      updateIntersectionDivAngle(app.syncApp);
    }
    projectionRotateAngle = null;
    for (const rotatedApp of appsRotatedSet.values()) {
      updateCanvasContext(rotatedApp);
      // drawOutlines(rotatedApp, _dicomBox);
    }
    // intersectionDiv.style.pointerEvents = "none";
  }
  // if (projectionTarget && projectionTarget.isHorizontal && projectionTarget.isVertical) {
  //   projectionTarget.style.background = "transparent";
  // }
  projectionTarget = null;
}

function updateIntersectionDivAngle(app) {
  const containerId = app.divId;
  const intersectionDivId = containerId + "-intersection";
  const intersectionDiv = document.getElementById(intersectionDivId);
  // let currentAngle = intersectionDiv.angle;
  // if (!currentAngle) currentAngle = 0;
  intersectionDiv.angle = projectionRotateAngle % 360;
  // console.log("intersectionDiv.angle", intersectionDiv.angle);
}

function projectionMouseMove(event) {
  if (projectionTarget && projectionTarget.app) {
    if (isProjectionMoving) {
      projectionMoving(event);
    } else if (isProjectionRotating) {
      projectionRotating(event);
    }
  }
}

/// !!! ROTATING
//#region Projection Rotating

function projectionRotateMouseDown(event) {
  appsRotatedSet.clear();
  let sourceElement = event.currentTarget;
  projectionTarget = sourceElement.parentElement;
  projectionRotateAngle = 0;
  if (projectionTarget) {
    isProjectionRotating = true;

    const containerDiv = projectionTarget.containerDiv;
    const containerRect = containerDiv.getBoundingClientRect();
    projectionRotateStartX = event.pageX - containerRect.left;
    projectionRotateStartY = event.pageY - containerRect.top;
  }
}

function projectionRotating(event) {
  let app = projectionTarget.app;
  let appArr = projectionTarget.appArr;
  if (vueApp.isAnnotationMode) {
    //(app == _appArr[0]) {
    appArr = _appArr[0].crossedApps;
  }

  const appLeft = app.projectionLeft;
  const appTop = app.projectionTop;
  const containerId = app.divId;
  const intersectionDivId = containerId + "-intersection";
  const intersectionDiv = document.getElementById(intersectionDivId);

  const containerDiv = event.currentTarget;
  const containerRect = containerDiv.getBoundingClientRect();
  const endX = event.pageX - containerRect.left;
  const endY = event.pageY - containerRect.top;

  let startX = projectionRotateStartX;
  let startY = projectionRotateStartY;
  const signedAngle = findSignedAngle(
    { x: startX, y: startY },
    { x: appLeft, y: appTop },
    { x: endX, y: endY },
  );

  let currentAngle = intersectionDiv.angle;
  if (!currentAngle) currentAngle = 0;
  currentAngle += signedAngle;
  currentAngle = +currentAngle.toFixed(4);
  projectionRotateAngle = currentAngle;

  updateAppTransformAngle(app, currentAngle);
  if (app.syncApp) {
    updateAppTransformAngle(app.syncApp, currentAngle);
  }

  if (app.index == 0) {
    // _appArr[0].isValue = -currentAngle;
    y3dRotation = currentAngle;
  } else if (app.index == 1) {
    // _appArr[0].lrValue = -currentAngle; // x axis
    x3dRotation = -currentAngle;
  } else if (app.index == 2) {
    // _appArr[0].paValue = -currentAngle;
    z3dRotation = -currentAngle;
  }

  for (const rotatedApp of appArr) {
    if (rotatedApp.index != app.index) {
      appsRotatedSet.add(rotatedApp);
    }
  }

  let xRad = THREE.MathUtils.degToRad(+x3dRotation);
  let yRad = THREE.MathUtils.degToRad(+z3dRotation);
  let zRad = THREE.MathUtils.degToRad(+y3dRotation);
  const euler = new THREE.Euler(xRad, -yRad, zRad, "XYZ");
  const dicomMatrix = new THREE.Matrix4();
  dicomMatrix.makeRotationFromEuler(euler);
  globalRotationMatrix = matrix4ToMatrix33(dicomMatrix);
  // console.log("globalRotationMatrix", globalRotationMatrix);
  // const rotatedApps = [...appsRotatedSet.values()];
  // console.log("updateAppToRotations", x3dRotation, z3dRotation, y3dRotation);
  for (const rotatedApp of appArr) {
    updateAppToRotations(rotatedApp);
  }

  let app0 = appArr[0];
  let app1 = appArr[1];
  let app2 = appArr[2];

  update3DFrame(app0);
  update3DFrame(app1);
  update3DFrame(app2);
}

function updateAppTransformAngle(app, newAngle) {
  const appLeft = app.projectionLeft;
  const appTop = app.projectionTop;
  const containerId = app.divId;
  const intersectionDivId = containerId + "-intersection";
  const intersectionDiv = document.getElementById(intersectionDivId);

  intersectionDiv.style.transformOrigin = `${appLeft}px ${appTop}px`;
  app.transformOrigin = { x: appLeft, y: appTop };

  intersectionDiv.style.transform = `rotate(${newAngle}deg)`;
  app.transformAngle = newAngle;

  //  console.log(
  //   "projectionRotateAngle",
  //   signedAngle,
  //   intersectionDiv.angle,
  //   currentAngle,
  // );
}

function updateAppToRotations(rotatedApp) {
  if (rotatedApp && appsRotatedSet && appsRotatedSet.has(rotatedApp)) {
    let xAngle = rotatedApp.index !== 1 ? x3dRotation : 0;
    let yAngle = rotatedApp.index !== 2 ? z3dRotation : 0;
    let zAngle = rotatedApp.index !== 0 ? y3dRotation : 0;

    destroyOutlines(rotatedApp);
    destroyAnnotationShapes(rotatedApp);
    const layerGroup = rotatedApp.getLayerGroupByDivId(rotatedApp.divId);
    const viewLayer = layerGroup.getActiveViewLayer();

    let rotationOrigin = null;
    let values = [0, 0, 0, 0];
    for (let appItem of _appArr) {
      if (!appItem.current) {
        values = null;
        break;
      }
      values[appItem.viewIndex] = appItem.current - 1;
    }

    if (values) {
      let app0 = _appArr[0];
      const indexPos = new dwv.Index(values);
      let size = app0.geometry.getSize();
      let originX = indexPos.get(0) / size.get(0);
      let originY = indexPos.get(1) / size.get(1);
      let originZ = indexPos.get(2) / size.get(2);
      rotationOrigin = [originX, originY, originZ];
    }

    viewLayer.setRotationAngles(
      xAngle, //_appArr[0].lrValue,
      yAngle, //_appArr[0].paValue,
      zAngle, //_appArr[0].isValue
      rotatedApp.index,
      rotationOrigin,
    );
    rotatedApp.isRotationApplied = true;
  }
}

function resetProjections() {
  console.log("resetProjections");
  try {
    appsRotatedSet.clear();
    x3dRotation = y3dRotation = z3dRotation = 0;
    globalRotationMatrix = null;
    let app0 = _appArr[0];
    let appArr = [..._appArr];
    if (app0.childApps) appArr = appArr.concat(app0.childApps);

    for (const app of appArr) {
      if (viewer && app.visualPlane3) {
        viewer.scene.remove(app.visualPlane3);
      }

      destroyOutlines(app);
      destroyAnnotationShapes(app);

      app.lrValue = app.paValue = app.isValue = 0;
      app.isRotationApplied = false;
      app.inversePointMatrix = app.pointMatrix = null;
      const layerGroup = app.getLayerGroupByDivId(app.divId);
      const viewLayer = layerGroup.getActiveViewLayer();
      viewLayer.resetRotationMatrix();

      const intersectionDivId = app.divId + "-intersection";
      let intersectionDiv = document.getElementById(intersectionDivId);
      intersectionDiv.style.transform = `rotate(0deg)`;
      app.transformAngle = 0;

      intersectionDiv.angle = 0;
      let centerPosition = Math.floor(app.max / 2) + 1;

      const appViewer = app.viewer;
      if (appViewer) {
        if (appViewer.applyCurrentTransforms) {
          appViewer.currentRotateAngle = 0;
          appViewer.currentFlipX = 1;
          appViewer.currentFlipY = 1;
          appViewer.applyCurrentTransforms();
        }
        appViewer.current = centerPosition;
      }

      app.resetZoom();
      if (app.current !== centerPosition) {
        app.current = centerPosition;
        setSliceIndex(app, app.current - 1);
        update3DFrame(app);
      } else {
        update3DFrame(app);
        updateCanvasContext(app);
      }
    }

    appArr = vueApp.isAnnotationMode ? app0.crossedApps : _appArr;
    for (const app of appArr) {
      updateSliceIntersection(app, appArr);
    }
  } catch (error) {}
}

//#endregion

function getDwvPos(vPosition, app) {
  const appGeometry = app.geometry;
  const dwvOrigin = appGeometry.getOrigin();
  let x = vPosition.x - _dicomBox.min.x + dwvOrigin.getX();
  let z = vPosition.y - _dicomBox.min.y + dwvOrigin.getZ();
  let y = _dicomBox.max.z - vPosition.z + dwvOrigin.getY();
  const dwvPosition = new dwv.Point([x, y, z, 0]);
  return dwvPosition;
}

function matrix4ToMatrix33(matrix4) {
  const m4Elements = matrix4.elements;
  const m = [
    m4Elements[0],
    m4Elements[4],
    m4Elements[8],
    m4Elements[1],
    m4Elements[5],
    m4Elements[9],
    m4Elements[2],
    m4Elements[6],
    m4Elements[10],
  ];
  return new dwv.Matrix33(m);
}

function projectionCircleMouseDown(event) {
  let sourceElement = event.currentTarget;
  // sourceElement.style.background = "white";
  projectionTarget = sourceElement; // .parentElement;
  if (projectionTarget) {
    let app = projectionTarget.app;
    isProjectionMoving = true;

    projectionOffsetX = event.pageX;
    projectionOffsetY = event.pageY;

    projectionCoordX = app.projectionLeft;
    projectionCoordY = app.projectionTop;
  }
}

function projectionMoveMouseDown(event) {
  let sourceElement = event.currentTarget;
  projectionTarget = sourceElement.parentElement;
  if (projectionTarget) {
    let app = projectionTarget.app;
    isProjectionMoving = true;

    projectionOffsetX = event.pageX;
    projectionOffsetY = event.pageY;

    projectionCoordX = app.projectionLeft;
    projectionCoordY = app.projectionTop;

    if (app.transformAngle && app.transformOrigin) {
      let origin = app.transformOrigin
        ? app.transformOrigin
        : { x: app.projectionLeft, y: app.projectionTop };
      let angle = app.transformAngle ? -app.transformAngle : 0;

      if (projectionTarget.isVertical) {
        projectionLineStart = rotate2dPoint(
          origin.x,
          origin.y,
          0,
          app.projectionTop,
          angle,
        );
        projectionLineEnd = rotate2dPoint(
          origin.x,
          origin.y,
          1000,
          app.projectionTop,
          angle,
        );
      } else if (projectionTarget.isHorizontal) {
        projectionLineStart = rotate2dPoint(
          origin.x,
          origin.y,
          app.projectionLeft,
          0,
          angle,
        );
        projectionLineEnd = rotate2dPoint(
          origin.x,
          origin.y,
          app.projectionLeft,
          1000,
          angle,
        );
      }
    }
  }
}

function projectionMoving(event) {
  if (!projectionTarget) return;

  let app = projectionTarget.app;
  let appArr = projectionTarget.appArr;
  if (app == _appArr[0]) {
    appArr = app.isAnnotating ? app.crossedApps : _appArr;
  }
  let appTop = app.projectionTop;
  let appLeft = app.projectionLeft;

  let newLeft = projectionCoordX + event.pageX - projectionOffsetX;
  let newTop = projectionCoordY + event.pageY - projectionOffsetY;

  if (+Math.abs(newTop - appTop) < 0.1 && +Math.abs(newLeft - appLeft) < 0.1)
    return;

  let currentLeft = app.projectionLeft;
  let currentTop = app.projectionTop;
  if (projectionTarget.isHorizontal) {
    currentTop = newTop;
  }
  if (projectionTarget.isVertical) {
    currentLeft = newLeft;
  }

  if (
    app.transformAngle &&
    app.transformOrigin &&
    (!projectionTarget.isHorizontal || !projectionTarget.isVertical)
  ) {
    let projectionPoint = projectPointOnLine(
      { x: newLeft, y: newTop },
      projectionLineStart,
      projectionLineEnd,
    );
    // console.log(`projection (${projectionPoint.x}, ${projectionPoint.y})`);
    // return;
    currentTop = projectionPoint.y;
    currentLeft = projectionPoint.x;
  }

  const layerGroup = app.getLayerGroupByDivId(app.divId);
  const viewLayer = layerGroup.getActiveViewLayer();

  let currentValues = [0, 0, 0, 0];
  for (let appItem of appArr) {
    if (!appItem.current) {
      currentValues = null;
      break;
    }
    if (!appItem.is3D || appItem.parentApp)
      currentValues[appItem.viewIndex] = appItem.current - 1;
  }

  if (viewLayer && currentValues) {
    let newIndex = viewLayer.displayToPlaneIndex(currentLeft, currentTop);
    let tempValues = newIndex.getValues();
    let newValues = [...currentValues];
    newValues[app.viewIndex] = app.current - 1;
    if (app.viewIndex == 2) {
      if (projectionTarget.isVertical) newValues[0] = tempValues[0];
      if (projectionTarget.isHorizontal) newValues[1] = tempValues[1];
    } else if (app.viewIndex == 1) {
      newValues[0] = tempValues[0];
      newValues[2] = tempValues[1];
    } else {
      newValues[1] = tempValues[0];
      newValues[2] = tempValues[1];
    }

    let anyChanges = false;
    for (let i = 0; i < newValues.length; i++) {
      if (newValues[i] != currentValues[i]) {
        anyChanges = true;
        break;
      }
    }

    if (anyChanges) {
      app.isProjectionMoving = true;
      app.projectionTop = currentTop;
      app.projectionLeft = currentLeft;

      for (let appItem of appArr) {
        if (appItem != app) {
          let currentValue = appItem.current - 1;
          let newValue = newValues[appItem.viewIndex];
          if (currentValue != newValue) {
            setSliceIndex(appItem, newValue);
          }
        }
      }

      app.isProjectionMoving = false;
    }
  }
}

function projectPointOnLine(P, A, B) {
  // P: point to project (e.g., {x: 5, y: 3})
  // A: first point defining the line (e.g., {x: 0, y: 0})
  // B: second point defining the line (e.g., {x: 10, y: 0})

  let AB = { x: B.x - A.x, y: B.y - A.y };
  let AP = { x: P.x - A.x, y: P.y - A.y };

  let dotProduct = AB.x * AP.x + AB.y * AP.y;
  let magnitudeSqAB = AB.x * AB.x + AB.y * AB.y;

  // Handle the case where A and B are the same point (line has no length)
  if (magnitudeSqAB === 0) {
    return A; // Or handle as an error, depending on requirements
  }

  let t = dotProduct / magnitudeSqAB;

  let D = { x: A.x + AB.x * t, y: A.y + AB.y * t };

  return D;
}

function updateNeighborIntersections(app, appArr) {
  if (!appArr) {
    appArr = app.isAnnotating ? app.crossedApps : _appArr;
  }

  appArr.forEach((appItem) => {
    updateSliceIntersection(appItem, appArr);
  });
}

function removeSliceIntersection(app) {
  if (app && app.divId) {
    const containerId = app.divId;
    const intersectionDivId = containerId + "-intersection";
    const intersectionDiv = document.getElementById(intersectionDivId);
    if (intersectionDiv) {
      intersectionDiv.style.display = "none";
      return;
    }

    const lineH = document.getElementById(
      containerId + "-intersection-horizontal",
    );
    if (lineH) lineH.style.display = "none"; // lineH.remove();

    const lineV = document.getElementById(
      containerId + "-intersection-vertical",
    );
    if (lineV) lineV.style.display = "none"; // lineV.remove();
  }
}

async function focusAnnotation(app, annotation) {
  console.log("focusAnnotation", annotation);
  for (const a of app.allAnnotations) {
    if (a != annotation) {
      a.isFocused = a.isEdited = a.isHovered = false;
      a.isHidden = true;
    }
  }
  annotation.isVisible = annotation.isFocused = true;
  annotation.isHidden = false;

  if (
    !annotation.isNerve &&
    app.editedAnnotation &&
    app.editedAnnotation != annotation
  ) {
    app.editedAnnotation.isEdited = false;
    await editAnnotation(app, app.editedAnnotation);
  }

  app.focusedAnnotation = null;
  let appArr = [app].concat(app.childApps);
  focusTooth(annotation.model, appArr);

  app.focusedAnnotation = annotation;

  for (const xapp of appArr) {
    drawAnnotations(xapp, _dicomBox);
  }

  focusAuxilaryModel(_annotationApp);
}

function unfocusAnnotation(mainApp, isMainCall) {
  // if (mainApp.focusedAnnotation) mainApp.focusedAnnotation.isFocused = false;

  mainApp.focusedAnnotation = null;
  if (!isMainCall) unfocusTooth(false);
  mainApp.allAnnotations.forEach((a) => {
    a.isHidden = false;
    a.isFocused = false;
  });
  let appArr = [mainApp].concat(mainApp.childApps);
  appArr.forEach((app) => {
    setSliceIndex(app, app.current - 1);
    _appArr.forEach((rapp) => {
      if (viewer && rapp.frameMesh) {
        viewer.scene.remove(rapp.frameMesh);
      }
    });
  });

  mainApp.allAnnotations.forEach((annotation) => {
    let mesh = viewer.get_model_mesh(annotation.model.id);
    if (mesh && mesh.visible !== undefined) {
      mesh.visible = annotation.isVisible && !annotation.model.isSingle;
    }
  });
}

//!!! focusDicomsTo3dPoint
function focusDicomsTo3dPoint(point, apps, force) {
  let anyChange = false;
  if (_dicomBox) {
    if (!apps) apps = _appArr;
    apps.forEach((app) => {
      // if (app.visible) {
      if (
        !force &&
        app.isAnnotating &&
        app.childApps &&
        app.focusedAnnotation &&
        app.focusedAnnotation.hasFigures
      ) {
        drawAnnotations(app, _dicomBox);
        return;
      }

      let diff = point.y - _dicomBox.min.y;
      let offset = diff / _dicomBox.size.y;
      if (app.index === 1) {
        diff = point.x - _dicomBox.min.x;
        offset = diff / _dicomBox.size.x;
      }
      if (app.index === 2) {
        diff = point.z - _dicomBox.min.z;
        offset = 1 - diff / _dicomBox.size.z;
      }

      let sliceIndex = Math.round(offset * app.max); //(app.max - 1));
      let newCurrent = sliceIndex + 1;

      if (app.current != newCurrent) {
        anyChange = true;
        setSliceIndex(app, sliceIndex);
        app.current = newCurrent;
      }
      // }
    });
  }
  return anyChange;
}

function assignFigure(app) {
  let sliceIndex = app.assignedSlice;
  let figure = app.assignedFigure;
  let oldAnnotation = app.assignedAnnotation;
  let newAnnotation = app.annotations.find((a) => a.isSelected);
  if (sliceIndex > 0 && figure && oldAnnotation && newAnnotation) {
    console.log("assignFigure", newAnnotation.filename);
    let oldFigures = oldAnnotation.allFigures[sliceIndex];
    let figureIndex = oldFigures.indexOf(figure);
    oldFigures.splice(figureIndex, 1);
    figure.annotation = newAnnotation;

    let newFigures = newAnnotation.allFigures[sliceIndex];
    if (!newFigures) {
      newAnnotation.allFigures[sliceIndex] = newFigures = [];
    }
    newFigures.push(figure);
    // newFigures.splice(figureIndex, 0, figure);

    if (sliceIndex === app.current) {
      oldAnnotation.figures.splice(figureIndex, 1);
      if (!newAnnotation.figures) {
        newAnnotation.figures = [];
      }
      newAnnotation.figures.push(figure);
      figure.color = newAnnotation.opacityColor;
      figure.filename = newAnnotation.filename;
      if (figure.spline) {
        let spline = figure.spline.getChildren()[0];
        spline.fill(figure.color);
        spline.stroke(figure.color);
      }
    }

    oldAnnotation.changed = oldAnnotation.wasChanged = true;
    oldAnnotation.hasRemovedFigures = true;

    newAnnotation.changed = newAnnotation.wasChanged = true;
  }
}

function changeEditMode(app, editMode, editSubMode) {
  console.log("changeEditMode", editMode, editSubMode);

  if (app.editMode !== editMode) {
    app.editMode = editMode;
    // app.isBrushMode = editMode === "brush";
    destroyAnnotationShapes(app);
    app.setToolFeatures({ editMode: editMode });
    drawAnnotations(app, _dicomBox);
  }

  if (app.editSubMode !== editSubMode) {
    app.editSubMode = editSubMode;
    app.setToolFeatures({ editSubMode: editSubMode });
  }
}

function updateDicomZipUrl(callbackAfterLoad) {
  let newZipUrl = vueApp.dicomZipUrl;
  if (store.state.dicom.isLowQuality) {
    newZipUrl = vueApp.dicomLowLink;
  }
  if (store.state.dicom.isMiddleQuality) {
    newZipUrl = vueApp.dicomMiddleLink;
  }
  if (store.state.dicom.isHighQuality) {
    newZipUrl = vueApp.dicomHighLink;
  }
  if (!_initialized || newZipUrl != vueApp.dicomZipUrl) {
    vueApp.dicomZipUrl = newZipUrl;
    console.log("updateDicomZipUrl", vueApp.dicomZipUrl);
    if (_initialized) {
      dicomSegmentation = {};
      // vueApp.isDicomLoaded = false;
      _appArr.forEach((app) => {
        app.baseSize = null;
        app.reset();
      });
      _loadedCounter = -1;

      let zipData = vueApp.dicomData[vueApp.dicomZipUrl];
      if (!zipData) {
        zipData = loadDicomZip(vueApp.dicomZipUrl, true);
      }
      processDicomImage(zipData);
    } else {
      loadDicoms(callbackAfterLoad);
    }
  }
}

async function getCephImgData(app) {
  if (!app)
    return null;
  console.log("getCephImgData");
  try {
    if (!app.cephImgData) {
      let imgUrl = `${vueApp.stlBaseUrl.replace("STL", "FA")}/ceph.png`;

      const response = await fetch(imgUrl);
      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob);
      const canvas = document.createElement("canvas");

      const layerGroup = app.getLayerGroupByDivId(app.divId);
      const viewLayer = layerGroup.getActiveViewLayer();
      const baseSize = viewLayer.getBaseSize();
      canvas.width = baseSize.x;
      canvas.height = baseSize.y;

      const ctx = canvas.getContext("2d");
      ctx.translate(canvas.width, canvas.height);
      ctx.scale(-1, -1);
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

      app.cephImgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      bitmap.close();

      // const cephImg = new Image();
      // cephImg.crossOrigin = "anonymous";
      // cephImg.onload = function () {
      //   canvasContext.drawImage(cephImg, 0, 0);
      // canvasContext.drawImage(img, 0, 0, canvas.width, canvas.height);
      // };
      // cephImg.src = "demo/ceph.png";
    }
  } catch (error) {
    console.log(`getCephImgData error: ${error}`);
  }

  return app.cephImgData;
}

export {
  _appArr as Apps,
  initChildApps,
  getGlobalCenter,
  assignFigure,
  addNewAnnotation,
  get3dSlicePoints,
  editAnnotation,
  editToothSettings,
  deleteAnnotation,
  focusAnnotation,
  unfocusAnnotation,
  updateAnnotationPosition,
  updateAppLayout,
  loadDicoms,
  loadDicomZip,
  updateOriginBox,
  update3DFrame,
  updateCanvasContext,
  setSliceIndex,
  setupCanvasEvents,
  updateSegmentation,
  updateAppToRotations,
  changeAppZoom,
  changeAppTool,
  changeEditMode,
  updateSliceIntersection,
  updateNeighborIntersections,
  switch3DView,
  switchTeethNumbers,
  closeViewer,
  focusDicomsTo3dPoint,
  removeSliceIntersection,
  attachAppEvents,
  updateDicomZipUrl,
  hideAllDicomPanels,
  switchToNextApp,
  loadDicomUrls,
  showLoaderPopup,
  resetProjections,
  _initialized as DicomInitialized,
  initDwvCommons,
  _dicomBox as DicomBox,
  convertDwvPosition,
  updateNervePoints,
  resetAppDivTransforms,
  getCephImgData,
};
